import {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ChevronDown, ChevronRight, Layers, Lock, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useFormContext, type FormContext } from '@/shared/lib/forms'

import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/utils'

import { formatCurrency } from '@/admin/lib/formatting'
import { useFormatters } from '@/shared/lib/formatting'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Separator } from '@/shared/components/ui/separator'
import { DataTable } from '@/admin/components/data-table'
import { DetailCard } from '@/admin/components/shared/detail-card'
import type { DataTableColumn } from '@/admin/components/data-table'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { AuthenticatedImage } from '@/admin/components/authenticated-image'
import useForm from '@/shared/lib/forms/useForm'
import type { FormField } from '@/shared/lib/forms'
import { defineField, isConditionallyRequired } from '@/shared/lib/forms'
import { asSearchPick, isBlankValue } from '@/shared/lib/forms/prefill'
import {
  useCreateLineItem,
  useUpdateLineItem,
  useDeleteLineItem,
  useMaterialPriceLookup,
  useLaborRateLookup,
  useMarkPricesRefreshed,
  useProductUomLookup,
  useSettlementClaimContext,
} from '@/admin/hooks/use-settlement'
import type {
  PayeeLookup,
  SettlementLineItem,
  SettlementSummary,
} from '@/admin/hooks/use-settlement'
import { toastApiError } from '@/shared/lib/api-error'
import {
  SAP_PAYEE_MAX_LENGTH,
  defaultLedgerForItem,
  dialogFieldsForItem,
  hasDialogFieldMapping,
  selectableOptions,
  getAllowedLedgerTypes,
  getAllowedPaymentTypes,
  itemDetailBadgeVariant,
  laborItemDetails,
  ledgerBadgeVariant,
  ledgerPillClass,
  legacyCategoryToItemDetail,
  materialItemDetails,
  materialPricingItemDetails,
  resolveCategory,
  workTypeSelectOptions,
} from '@/admin/lib/settlement-options'
import { useSettlementOptions } from '@/admin/hooks/use-settlement'
import { useClaim, useEditClaimDirect } from '@/admin/hooks/use-claims'
import {
  useHolding,
  useProductTemplates,
  useProducts,
  useProductVariants,
} from '@/admin/hooks/use-products'
import {
  toAddressInputValue,
  type EditableAddressValue,
} from '@/shared/lib/formatting/address'
import {
  SkuSearchCombobox,
  type SkuVariantMatch,
} from '@/admin/components/claims/sku-search-combobox'
import { useEntityTypeByKey } from '@/admin/hooks/use-entities'
import { useClaimAttachments } from '@/admin/pages/claims/use-claim-attachments'

// Dialog fields whose presence is driven by the tenant's
// ``settlement_line_item`` entity registration: a field renders only
// when the tenant's registry defines an attribute with the same
// code_name. Core capture fields (item_detail, description, quantity,
// unit_price, notes) always render. Tenants with a registry that
// defines everything (e.g. OC's SAP-aligned set) see the full dialog;
// a minimal registry produces a minimal dialog - no per-tenant code.
const REGISTRY_GATED_FIELDS = new Set([
  'voucher_quantity',
  'additional_amount',
  'total_labor_rate',
  'reason_for_increase',
  'product_code',
  'sap_short_code',
  'product_line',
  'product_name',
  'color',
  'work_type',
  'uom',
  'ledger_type',
  'recipient_type',
  'payee',
  'payee_address',
  'payment_type',
  'payment_date',
])

// Fallback field visibility for a tenant that has authored no per-item
// ``dialog_fields`` mapping: the fields that only make sense for a material
// line, and the ones that only make sense for a labor line. Anything not
// named here shows for every item detail, as it always has.
const MATERIAL_FALLBACK_FIELDS = new Set([
  'product_code',
  'sap_short_code',
  'product_line',
  'product_name',
  'color',
  'voucher_quantity',
])

const LABOR_FALLBACK_FIELDS = new Set([
  'work_type',
  'additional_amount',
  'total_labor_rate',
  'reason_for_increase',
])

const WHO_WHEN_FIELDS = [
  'recipient_type',
  'payee',
  'payee_address',
  'payment_type',
  'payment_date',
]

// The composite-address keys the ADDRESS field stores (camelCase). Reduce a
// captured payee address to exactly these, dropping the transient
// ``pendingSearch`` marker, and treat an all-blank address as "no value" so we
// never persist an empty {} on the line item.
const PAYEE_ADDRESS_KEYS = [
  'addressLine1',
  'addressLine2',
  'locality',
  'region',
  'postalCode',
  'countryCode',
] as const

function toPayeeAddressPayload(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object') return null
  const source = value as Record<string, unknown>
  const out: Record<string, string> = {}
  let hasAny = false
  for (const key of PAYEE_ADDRESS_KEYS) {
    const raw = source[key]
    // Coerce any non-null value (a lookup provider may return a numeric
    // postcode) rather than only accepting strings.
    const str = raw !== null && raw !== undefined ? String(raw).trim() : ''
    out[key] = str
    if (str) hasAny = true
  }
  return hasAny ? out : null
}


/**
 * The payee search for one recipient type (WPS-263): the claim field's own
 * search, with its sibling-fill retargeted at this form - the picked name
 * fills Payee, and the record value the claim maps to its address field
 * fills Payee Address. Only listed records: an unlisted payee is typed.
 */
function payeeLookupConfig(lookup: PayeeLookup): Record<string, unknown> {
  const { prefill_map: claimPrefill, ...rest } = lookup.configuration_data as {
    prefill_map?: Record<string, string>
  } & Record<string, unknown>
  const addressPath = lookup.address_code
    ? claimPrefill?.[lookup.address_code]
    : undefined
  return {
    ...rest,
    allow_create: false,
    prefill_map: {
      payee: 'label',
      ...(addressPath ? { payee_address: addressPath } : {}),
    },
  }
}

interface Props {
  claimUuid: string
  lineItems: SettlementLineItem[]
  isEditable: boolean
  currency?: string
  /**
   * Settlement summary (when available) so the expandable row detail can
   * show each line's amount after its category's proration coverage. The
   * authoritative prorated totals still come from the summary card; this
   * is a per-line breakdown derived from the same proration percentages.
   */
  summary?: SettlementSummary | null
  /** WP1-62: when prices were last refreshed (ISO string), for the timestamp + lock. */
  pricesRefreshedAt?: string | null
  /**
   * WP1-62: true once the claim has moved past the editable settlement stage
   * (i.e. sent to LOA / beyond), so price refresh is locked. Driven by the
   * claim's stage, NOT by the viewer's permission - a user who simply lacks
   * edit rights on a still-editable claim must NOT see the "sent to LOA" lock.
   */
  isLocked?: boolean
  /**
   * WPS-119: the payment type a NEW line starts on, from the claim type's
   * config. Some claim types settle exactly one way - BFS is always paid to
   * its third-party installer by cheque or ACH - so pre-selecting it turns a
   * required question with one answer into a value the RS can simply confirm.
   *
   * Only a starting value: the RS can change it, and the item-detail effect
   * below still clears it if the detail they pick does not allow it. Null for
   * every claim type that names no default, which is the common case.
   */
  defaultPaymentType?: string | null
}

// ── Expandable Detail Row ───────────────────────────────

/**
 * One label/value pair in an expanded line item, stacked rather than inline.
 *
 * Inline pairs at 9px/11px packed the panel into an unreadable run of text that
 * had to be picked apart word by word. Stacked, in a grid, each value sits
 * under its own heading and the eye can scan a column - and the sizes are ones
 * people can actually read (the label is the smallest thing here at 11px, and
 * the value it describes is 14px).
 */
function DetailField({
  label,
  value,
  span,
}: {
  label: string
  value: ReactNode
  /** Let a long value (Notes) take the full row instead of being squeezed. */
  span?: boolean
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div className={cn('flex flex-col gap-0.5', span && 'col-span-full')}>
      <span className="text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)]">
        {label}
      </span>
      <span className="text-[14px] font-light leading-snug text-[var(--ink-1)]">
        {value}
      </span>
    </div>
  )
}

/**
 * A titled block of detail fields. Renders nothing when every field inside it
 * resolved to empty, so a line item without payment details does not leave a
 * bare "Payment" heading over blank space.
 */
function DetailGroup({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  const hasContent = Children.toArray(children).some(Boolean)
  if (!hasContent) return null
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--ink-2)]">
        {title}
      </span>
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
        {children}
      </div>
    </div>
  )
}

// WP1-60: "Date Added" = the line item's created_at, shown MM/DD/YYYY HH:MM:SS.
// Uses the Intl API (no manual padding) per the repo style guide. Takes the
// org locale from the caller (useFormatters()) since this is a module-level
// helper, not a hook.
/**
 * Render a stored quantity as a plain human number.
 *
 * DRF serialises a DecimalField as a fixed-precision string ("2.0000"), which
 * reads as noise in a table. Empties are guarded first because Number(null)
 * and Number('') both coerce to 0 rather than NaN, which would print a
 * confident "0" where there is in fact no value.
 */
/**
 * True when an Additional Amount field holds a real increase. Blank, zero and
 * unparseable all mean "no uplift", so the Reason for Increase stays hidden
 * and unrequired.
 */
function hasAdditionalAmount(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false
  const amount = Number(value)
  return Number.isFinite(amount) && amount !== 0
}

function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-'
  const quantity = Number(value)
  return Number.isFinite(quantity) ? quantity.toLocaleString() : String(value)
}

/**
 * Read a stored money EAV value as a number, treating blank and zero as "no
 * value" so a nil uplift never renders a panel explaining itself.
 */
function toAmount(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const amount = Number(value)
  return Number.isFinite(amount) && amount !== 0 ? amount : null
}

function formatDateAdded(value: string | null | undefined, locale: string): string | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const datePart = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
  const timePart = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(d)
  return `${datePart} ${timePart}`
}

// Per-hose evidence thumbnails on the parent settlement row. A multi-item
// (umbrella) row links to its child hose claim via ``source_child_claim``; the
// hose's photos live on that child, so we fetch them by child UUID and reuse
// the same signed thumbnail URLs the Evidence card renders. Mounted only when a
// row is expanded (so the query runs lazily, per hose).
function HoseEvidenceThumbnails({ claimUuid }: { claimUuid: string }) {
  const { data: attachments } = useClaimAttachments(claimUuid)
  const photos = (attachments ?? []).filter(
    (a) => !a.is_deleted && a.thumbnail_url,
  )
  if (photos.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[9px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)]">
        Hose photos
      </span>
      <div className="flex flex-wrap gap-1.5">
        {photos.map((att) => (
          <a
            key={att.uuid}
            href={att.file_url ?? att.thumbnail_url ?? '#'}
            target="_blank"
            rel="noreferrer"
            title={att.file_name}
            className="block h-12 w-12 overflow-hidden rounded-md border-[0.5px] border-[var(--rule)]"
          >
            <AuthenticatedImage
              src={att.thumbnail_url ?? att.file_url}
              alt={att.file_name}
              className="h-full w-full"
            />
          </a>
        ))}
      </div>
    </div>
  )
}

function ExpandedDetailRow({
  item,
  options,
  currency = 'USD',
}: {
  item: SettlementLineItem
  options?: ReturnType<typeof useSettlementOptions>['data']
  currency?: string
}) {
  const { locale } = useFormatters()
  const { t } = useTranslation()
  // WPS-209 #12: the per-line "After Proration" readout used to live here, in
  // the expanded row. It now has a dedicated Prorated Amount column (WPS-208
  // #11), and Patrick's Example 12-1 marks this block as the highlight that
  // "can go away" once that column exists - so the figure is stated once, in
  // the table, where it can be compared across lines. Prorated Qty and
  // Prorated Amount are both columns now; nothing is lost by dropping it here.
  // Payment Type (its own column) and Company Code (derived) are not repeated
  // here either (WPS-175 7A).
  const ledgerLabel = item.ledger_type
    ? options?.ledger_types.find((l) => l.value === item.ledger_type)?.label ?? item.ledger_type
    : null

  const profitLabel = item.profit_centre
    ? options?.profit_centres.find((p) => p.value === item.profit_centre)?.label ?? item.profit_centre
    : null

  const recipientTypeLabel = item.recipient_type
    ? options?.recipient_types?.find((r) => r.value === item.recipient_type)?.label ?? item.recipient_type
    : null

  const uomLabel = item.uom
    ? options?.uoms?.find((u) => u.value === item.uom)?.label ?? item.uom
    : null

  // X8: product facts follow the Item Detail's ``dialog_fields`` mapping, so a
  // line that captures no product (Non-OC Material) shows none. No mapping = all.
  const mappedFields = dialogFieldsForItem(item.item_detail ?? '', options)
  const captures = (name: string) => !mappedFields || mappedFields.has(name)

  // A zero uplift is not an uplift, so it does not earn the panel.
  const additionalAmount = toAmount(item.additional_amount)
  const systemRate = toAmount(item.labor_system_rate)
  const totalLaborRate = toAmount(item.total_labor_rate)
  const reasonForIncreaseLabel = item.reason_for_increase
    ? options?.reasons_for_increase?.find(
        (r) => r.value === item.reason_for_increase,
      )?.label ?? item.reason_for_increase
    : null

  return (
    <div className="flex flex-col gap-4 py-1">
      {/* Grouped rather than one undifferentiated run: what was bought, who is
          paid, and where it books are three different questions, and a reader
          looking for one of them should not have to read the other two. */}
      <DetailGroup title={t('settlement.lineItems.productGroup', 'Product')}>
        <DetailField label={t('settlement.lineItems.productLine', 'Product Line')} value={captures('product_line') ? item.product_line : null} />
        <DetailField label={t('settlement.lineItems.productName', 'Product Name')} value={captures('product_name') ? item.product_name : null} />
        <DetailField label={t('settlement.lineItems.color', 'Color')} value={captures('color') ? item.color : null} />
        <DetailField label={t('settlement.lineItems.sapCode', 'SAP Code')} value={captures('sap_short_code') ? item.sap_short_code : null} />
        <DetailField label={t('settlement.lineItems.workType', 'Work Type')} value={captures('work_type') ? item.work_type : null} />
        {/* WPS-175 §9 moved UOM off the fixed column set; it belongs with the
            rest of the product facts rather than competing with the figures. */}
        <DetailField label={t('settlement.lineItems.unitOfMeasure', 'Unit of Measure')} value={captures('uom') ? uomLabel : null} />
      </DetailGroup>

      {/* WPS-175 §8: a labour line paid above the published rate has to show
          the uplift, not just the final number - the published rate, what was
          added, and why. Rendered when there is an uplift or a recorded reason
          (WPS-207: a Labor line can carry a reason with no increase). */}
      {(additionalAmount != null || reasonForIncreaseLabel) && (
        <DetailGroup title={t('settlement.lineItems.laborRateGroup', 'Labor Rate')}>
          <DetailField
            label={t('settlement.lineItems.systemRate', 'System Rate')}
            value={
              systemRate != null
                ? formatCurrency(systemRate, currency, locale)
                : null
            }
          />
          <DetailField
            label={t('settlement.lineItems.additionalAmount', 'Additional Amount')}
            value={
              additionalAmount != null
                ? formatCurrency(additionalAmount, currency, locale)
                : null
            }
          />
          <DetailField
            label={t('settlement.lineItems.totalLaborRate', 'Total Labor Rate')}
            value={
              totalLaborRate != null
                ? formatCurrency(totalLaborRate, currency, locale)
                : null
            }
          />
          <DetailField label={t('settlement.lineItems.reasonForIncrease', 'Reason for Increase')} value={reasonForIncreaseLabel} />
        </DetailGroup>
      )}

      <DetailGroup title={t('settlement.lineItems.paymentGroup', 'Payment')}>
        <DetailField label={t('settlement.lineItems.recipient', 'Recipient')} value={recipientTypeLabel} />
        <DetailField label={t('settlement.lineItems.payee', 'Payee')} value={item.payee} />
        <DetailField label={t('settlement.lineItems.paymentDate', 'Payment Date')} value={item.payment_date} />
      </DetailGroup>

      <DetailGroup title={t('settlement.lineItems.accountingGroup', 'Accounting')}>
        <DetailField label={t('settlement.lineItems.ledger', 'Ledger')} value={ledgerLabel} />
        <DetailField label={t('settlement.lineItems.profitCentre', 'Profit Centre')} value={profitLabel} />
        <DetailField
          label={t('settlement.lineItems.dateAdded', 'Date Added')}
          value={formatDateAdded(item.created_at, locale)}
        />
        <DetailField label={t('settlement.lineItems.notes', 'Notes')} value={item.notes} span />
      </DetailGroup>
      {/* Multi-item (umbrella) row: show the hose's own evidence photos,
          fetched from its child claim. */}
      {item.source_child_claim && (
        <HoseEvidenceThumbnails claimUuid={item.source_child_claim} />
      )}
    </div>
  )
}

// Keep parent cascade query state synced to current form product values.
function CascadeSync({
  onSync,
}: {
  onSync: (values: { product_line: string | null; product_name: string | null }) => void
}) {
  const { values } = useFormContext()
  const productLine = ((values.product_line as string | undefined) || '').trim() || null
  const productName = ((values.product_name as string | undefined) || '').trim() || null
  useEffect(() => {
    onSync({ product_line: productLine, product_name: productName })
  }, [productLine, productName, onSync])
  return null
}

/**
 * Keep Total Labor Rate equal to the published rate plus whatever was added
 * (WPS-175 §8).
 *
 * Kept as a mounted watcher rather than an onChange on each of the two source
 * fields, so the total is right no matter which of them moved - including when
 * the work-type cascade fills the published rate asynchronously, which no
 * onChange on Additional Amount would ever see.
 */
function LaborRateSync() {
  const { values, setFieldValue } = useFormContext()
  const unitPrice = values.unit_price
  const additional = values.additional_amount
  useEffect(() => {
    const base = Number(unitPrice)
    if (!Number.isFinite(base) || unitPrice === '' || unitPrice == null) return
    const extra = hasAdditionalAmount(additional) ? Number(additional) : 0
    const total = (base + extra).toFixed(2)
    if (String(values.total_labor_rate ?? '') !== total) {
      setFieldValue('total_labor_rate', total)
    }
    // ``values.total_labor_rate`` is read to avoid a redundant write, not to
    // drive the effect - including it would re-run on our own update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitPrice, additional, setFieldValue])
  return null
}

// ── Main Component ──────────────────────────────────────

export function SettlementLineItemsTable({
  claimUuid,
  lineItems,
  isEditable,
  currency = 'USD',
  summary,
  pricesRefreshedAt,
  isLocked = false,
  defaultPaymentType = null,
}: Props) {
  const { t } = useTranslation()
  const { locale } = useFormatters()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<SettlementLineItem | null>(null)
  const [expandedUuids, setExpandedUuids] = useState<Set<string>>(new Set())
  // WP1-60: line items are expanded by default. Each item is auto-expanded
  // once (on first appearance); later user collapses are respected.
  const seenUuidsRef = useRef<Set<string>>(new Set())
  // WP1-60: queue an automatic price refresh after add/update.
  const autoRefreshRef = useRef(false)
  // WP1-62: auto-refresh once when the settlement is opened while editable,
  // so stale rates can never reach LOA / settlement unnoticed.
  const didLoadRefreshRef = useRef(false)

  // Runs on every lineItems change. Two concerns share the dependency, so
  // they live in one effect (per the repo style guide):
  //  1. WP1-60: expand each line item by default the first time it appears
  //     (ref mutated in the body, not the setState updater, so StrictMode's
  //     double-invoke of the updater stays pure).
  //  2. WP1-60: after an add/update lands (lineItems refetched), run a queued
  //     price refresh once.
  useEffect(() => {
    const fresh = lineItems
      .map((li) => li.uuid)
      .filter((uuid) => !seenUuidsRef.current.has(uuid))
    if (fresh.length > 0) {
      fresh.forEach((uuid) => seenUuidsRef.current.add(uuid))
      setExpandedUuids((prev) => {
        const next = new Set(prev)
        fresh.forEach((uuid) => next.add(uuid))
        return next
      })
    }
    if (autoRefreshRef.current) {
      autoRefreshRef.current = false
      void handleRefreshPrices()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems])

  // WP1-62: auto-refresh prices once on open while the claim is still
  // editable (pre-LOA). Silent (no toast) so simply viewing the claim
  // doesn't nag; it stamps the refresh time only when a price changes.
  // Stops automatically at LOA because the claim is no longer editable.
  useEffect(() => {
    if (didLoadRefreshRef.current) return
    if (!isEditable || lineItems.length === 0) return
    didLoadRefreshRef.current = true
    void handleRefreshPrices({ silent: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable, lineItems.length])

  const createMut = useCreateLineItem(claimUuid)
  const updateMut = useUpdateLineItem(claimUuid)
  const deleteMut = useDeleteLineItem(claimUuid)
  const { data: options } = useSettlementOptions(claimUuid)
  // The tenant's line-item registration drives which dialog fields
  // exist for them (see REGISTRY_GATED_FIELDS). An empty/unloaded
  // registry falls back to the full legacy dialog.
  const { data: lineItemEntity } = useEntityTypeByKey('settlement_line_item')
  const registryCodes = useMemo(
    () => new Set((lineItemEntity?.attributes ?? []).map((a) => a.code_name)),
    [lineItemEntity],
  )
  const materialPriceLookup = useMaterialPriceLookup(claimUuid)
  const laborRateLookup = useLaborRateLookup(claimUuid)
  const markPricesRefreshed = useMarkPricesRefreshed(claimUuid)
  const [refreshing, setRefreshing] = useState(false)
  const itemDetailOptions = useMemo(() => options?.item_details ?? [], [options])
  // Pickers offer only the still-selectable options; the full lists above stay
  // the label source so a saved row on a retired option still reads correctly
  // (WPS-147 retired the "Goodwill" / "Sales Goodwill" item details and the
  // single legacy "Goodwill" ledger).
  const selectableItemDetails = useMemo(
    () => selectableOptions(options?.item_details),
    [options],
  )
  const selectableLedgerTypes = useMemo(
    () => selectableOptions(options?.ledger_types),
    [options],
  )

  // WPS-147 §3: capped ledgers (OC caps RS Goodwill at $1,000 per claim) show
  // how much of the ceiling this claim has already used, so the RS sees the
  // limit before the save is rejected. Cap + label are option config; the
  // booked amount is summed off the rows already on screen.
  const ledgerHint = useMemo(() => {
    const capped = selectableLedgerTypes.filter(
      (option) => option.cap_amount !== null && option.cap_amount !== undefined,
    )
    const fallback = t(
      'claims.settlement.ledgerHint.default',
      'Auto-defaulted from item detail',
    )
    if (capped.length === 0) return fallback
    const parts = capped.map((option) => {
      const booked = lineItems
        .filter((row) => row.ledger_type === option.value)
        .reduce((sum, row) => sum + Number(row.extended_price ?? 0), 0)
      const cap = Number(option.cap_amount)
      return t(
        'claims.settlement.ledgerHint.capUsed',
        '{{label}}: {{booked}} of {{cap}} cap used',
        {
          label: option.label,
          booked: formatCurrency(booked, currency, locale),
          cap: formatCurrency(cap, currency, locale),
        },
      )
    })
    return t(
      'claims.settlement.ledgerHint.withCaps',
      'Auto-defaulted from item detail. {{caps}}.',
      { caps: parts.join('. ') },
    )
  }, [selectableLedgerTypes, lineItems, currency, locale, t])
  const uomOptions = useMemo(() => options?.uoms ?? [], [options])
  const ledgerTypeOptions = useMemo(() => options?.ledger_types ?? [], [options])
  // WPS-165 #10: the API says which work types this claim's area actually
  // publishes a rate for; the unpriced ones are named rather than removed.
  const workTypeChoices = useMemo(
    () =>
      workTypeSelectOptions(options, (label) =>
        t(
          'settlement.lineItems.workTypeNoPublishedRate',
          '{{label}} (no published rate)',
          { label },
        ),
      ),
    [options, t],
  )
  const recipientTypeOptions = useMemo(() => options?.recipient_types ?? [], [options])
  const categoryOptions = useMemo(() => options?.categories ?? [], [options])
  const coverageDecisionOptions = useMemo(
    () => options?.coverage_decisions ?? [],
    [options],
  )
  // Tenants without the fine-grained item_detail vocabulary (e.g. Teknor
  // multi-hose) fall back to the simple 3-way category dropdown.
  const hasItemDetails = itemDetailOptions.length > 0
  const hasCoverageDecisions = coverageDecisionOptions.length > 0

  const materialItemDetailsSet = useMemo(() => materialItemDetails(options), [options])
  const laborItemDetailsSet = useMemo(() => laborItemDetails(options), [options])
  // WPS-162: item details that source product + price from the Material Pricing
  // Table (data-driven off the option's ``product_source`` marker, never a
  // tenant slug). For these, the dialog replaces the product-library cascade
  // with a single Product Base picker fed by ``material_products``.
  const materialPricingItemDetailsSet = useMemo(
    () => materialPricingItemDetails(options),
    [options],
  )
  // The product bases the pricing table publishes for this claim's geozone,
  // as the Product Base picker options, plus a lookup back to each base's
  // representative rate (its product line, unit, sap code, price).
  const materialProducts = useMemo(
    () => options?.material_products ?? [],
    [options],
  )
  const materialBaseOptions = useMemo(
    () => materialProducts.map((p) => ({ value: p.value, label: p.label })),
    [materialProducts],
  )
  const materialBaseByValue = useMemo(() => {
    const map = new Map<string, (typeof materialProducts)[number]>()
    for (const p of materialProducts) map.set(p.value, p)
    return map
  }, [materialProducts])
  const reasonForIncreaseOptions = useMemo(
    () => options?.reasons_for_increase ?? [],
    [options],
  )
  // A ``required_when`` rule the tenant authored on the reason (WPS-207: OC
  // requires it on every Labor line, increase or not).
  const reasonForIncreaseConfig = useMemo(() => {
    const rule = options?.required_when?.reason_for_increase
    return rule ? { required_when: rule } : undefined
  }, [options])

  // WPS-175 §7: which fields the Add Line Item card shows for the chosen Item
  // Detail. The mapping is authored per option (``dialog_fields``); a tenant
  // that has authored none falls back to the older behaviour, where the
  // option's kind decided - material items get the product cascade, labor
  // items get the work type. Both paths are data-driven; neither names a
  // tenant.
  const showsField = useCallback(
    (itemDetail: string, name: string) => {
      const mapped = dialogFieldsForItem(itemDetail, options)
      if (mapped) return mapped.has(name)
      if (MATERIAL_FALLBACK_FIELDS.has(name)) {
        return materialItemDetailsSet.has(itemDetail)
      }
      if (LABOR_FALLBACK_FIELDS.has(name)) {
        return laborItemDetailsSet.has(itemDetail)
      }
      return true
    },
    [options, materialItemDetailsSet, laborItemDetailsSet],
  )

  // Reason for Increase is asked once there is an increase to explain, or
  // wherever the tenant's ``required_when`` rule requires it.
  const asksReasonForIncrease = useCallback(
    (itemDetail: string, values: Record<string, unknown>) =>
      showsField(itemDetail, 'reason_for_increase') &&
      (hasAdditionalAmount(values.additional_amount) ||
        isConditionallyRequired(reasonForIncreaseConfig, values)),
    [showsField, reasonForIncreaseConfig],
  )

  // WPS-175 §7: what this claim already knows - the product line(s) it may
  // spend against, the quantity a line should start at, and the payee it
  // recorded. All resolved server-side from configuration.
  const { data: claimContext } = useSettlementClaimContext(claimUuid)
  const allowedProductLines = useMemo(
    () => new Set(claimContext?.allowed_product_lines ?? []),
    [claimContext],
  )
  const uomLookup = useProductUomLookup(claimUuid)
  // WPS-175 §7: whether the selected product has an approved unit of measure.
  // ``null`` while nothing has been looked up yet (a fresh dialog, or a
  // non-material line), ``false`` once a lookup came back empty - which is
  // what blocks the save.
  const [uomResolved, setUomResolved] = useState<boolean | null>(null)
  // WPS-165 #10: whether the last labour-rate lookup found a published rate.
  // ``null`` while nothing has been asked (a fresh dialog, or a non-labour
  // line), ``false`` once a lookup came back empty. Unlike the unit-of-measure
  // check above this never blocks the save - the operator may legitimately
  // hold a rate the book has not been loaded with - it only says so out loud
  // instead of leaving Unit Price mysteriously blank.
  const [laborRateResolved, setLaborRateResolved] = useState<boolean | null>(null)
  // Whether the approved product-to-unit mapping has any content at all.
  // Absent (older API) reads as false, so the block stays dormant rather
  // than firing against a payload that cannot answer the question.
  const uomMappingAvailable = options?.uom_mapping_available === true

  const quantityHint = useMemo(() => {
    if (claimContext?.default_quantity == null) return undefined
    // Naming the source matters: 'approved' means the lab narrowed the claim
    // and this is the prorated figure, which is a different assertion from
    // the registered roof or what the claim itself recorded.
    switch (claimContext.default_quantity_source) {
      case 'approved':
        return t('settlement.lineItems.quantityHintApproved', 'Prefilled with the approved quantity for this claim')
      case 'claim':
        return t('settlement.lineItems.quantityHintClaim', 'Prefilled from the quantity recorded on this claim')
      default:
        return t('settlement.lineItems.quantityHintRegistration', 'Prefilled from the registration')
    }
  }, [claimContext, t])


  // Says why Unit Price is empty on a labour line whose work type the book
  // does not price. Silence here is what makes a missing rate look like a
  // broken field, and an operator who types a rate over it leaves no trace
  // that it was never the approved one.
  const unitPriceHint = useMemo(
    () =>
      laborRateResolved === false
        ? t(
            'settlement.lineItems.unitPriceHintNoLaborRate',
            'No published labor rate for this work type - enter the rate manually',
          )
        : undefined,
    [laborRateResolved, t],
  )

  // Resolve the approved unit for a product and mirror it into the form.
  // Best-effort on the network, but decisive on the answer: an empty result
  // clears the unit and marks the line unsaveable rather than leaving a stale
  // unit from the previously selected product in place.
  const applyProductUom = useCallback(
    async (
      formContext: FormContext,
      productLine: string,
      productBase?: string | null,
      sku?: string | null,
    ) => {
      // A base alone is enough: the unit is read from the product itself.
      if (!productLine && !productBase) {
        setUomResolved(null)
        return
      }
      const result = await uomLookup.lookup(productLine, productBase, sku)
      if (result?.uom) {
        formContext.setFieldValue('uom', result.uom)
        setUomResolved(true)
        return
      }
      formContext.setFieldValue('uom', '')
      // ``false`` blocks the save, so only say it when the server says the
      // rule applies. A tenant that publishes no units at all, or a lookup
      // that failed outright, leaves this ``null`` - unknown, not refused.
      setUomResolved(result && result.enforced ? false : null)
    },
    [uomLookup],
  )

  // ── Product library hooks for cascading dropdowns ──────
  const [selectedProductLine, setSelectedProductLine] = useState<string | null>(null)
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null)

  const { data: productTemplates } = useProductTemplates()

  const templateNameToUuid = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of productTemplates ?? []) map.set(t.name, t.uuid)
    return map
  }, [productTemplates])

  const selectedTemplateUuid = useMemo(
    () => (selectedProductLine ? templateNameToUuid.get(selectedProductLine) ?? null : null),
    [selectedProductLine, templateNameToUuid],
  )

  const productsParams = useMemo(
    () => (selectedTemplateUuid ? { template_uuid: selectedTemplateUuid, page_size: '500' } : undefined),
    [selectedTemplateUuid],
  )
  const { data: productsResponse } = useProducts(productsParams, {
    enabled: !!selectedTemplateUuid,
  })

  const productNameToUuid = useMemo(() => {
    const map = new Map<string, string>()
    for (const p of productsResponse?.results ?? []) {
      if (p.product_type === 'VARIANT_BASE' || p.product_type === 'PRODUCT') {
        map.set(p.cached_short_name, p.uuid)
      }
    }
    return map
  }, [productsResponse])

  const selectedProductUuid = useMemo(
    () => (selectedProductName ? productNameToUuid.get(selectedProductName) ?? null : null),
    [selectedProductName, productNameToUuid],
  )

  const { data: variants } = useProductVariants(selectedProductUuid)

  // WPS-175 §7: a settlement may only spend against the product line the claim
  // was opened with, so a shingle claim never offers Lumber or Insulation.
  // Narrowed by the claim context; an unconfigured claim keeps the full list.
  const productLineOptions = useMemo(() => {
    const all = (productTemplates ?? []).map((t) => ({ value: t.name, label: t.name }))
    if (allowedProductLines.size === 0) return all
    const narrowed = all.filter((option) => allowedProductLines.has(option.value))
    // Never narrow to nothing: a mapping that matches no template the tenant
    // actually has would leave the operator unable to record anything at all.
    return narrowed.length > 0 ? narrowed : all
  }, [productTemplates, allowedProductLines])

  // A claim allowed exactly one product line has nothing to choose there, so
  // the dialog fills it in and the product list is ready at once (WPS-119).
  const onlyProductLine = useMemo(
    () =>
      allowedProductLines.size > 0 && productLineOptions.length === 1
        ? productLineOptions[0].value
        : null,
    [allowedProductLines, productLineOptions],
  )

  const productNameOptions = useMemo(() => {
    const items = productsResponse?.results ?? []
    return items
      .filter((p) => p.product_type === 'VARIANT_BASE' || p.product_type === 'PRODUCT')
      .map((p) => ({ value: p.cached_short_name, label: p.cached_short_name }))
  }, [productsResponse])

  const colorOptions = useMemo(() => {
    if (!variants) return []
    const seen = new Set<string>()
    const opts: { value: string; label: string }[] = []
    for (const v of variants) {
      const colorVal =
        (v.product_data?.color as string | undefined) || v.cached_short_name
      if (colorVal && !seen.has(colorVal)) {
        seen.add(colorVal)
        opts.push({ value: colorVal, label: colorVal })
      }
    }
    return opts
  }, [variants])

  const handleOpenEdit = useCallback((item: SettlementLineItem) => {
    setEditing(item)
    setSelectedProductLine(item.product_line || null)
    setSelectedProductName(item.product_name || null)
    setDialogOpen(true)
  }, [])

  const handleCascadeSync = useCallback(
    ({ product_line, product_name }: { product_line: string | null; product_name: string | null }) => {
      setSelectedProductLine((prev) => (prev === product_line ? prev : product_line))
      setSelectedProductName((prev) => (prev === product_name ? prev : product_name))
    },
    [],
  )

  const moneyField = useMemo(
    () => defineField({ code_name: '_money', display_name: '', field_type: 'MONEY' }),
    [],
  )

  const toggleExpand = useCallback((uuid: string) => {
    setExpandedUuids((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }, [])

  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)
  const handleDelete = useCallback(
    (uuid: string) => {
      deleteMut.mutate(uuid, {
        onSuccess: () => {
          toast.success('Line item removed')
          setDeletingUuid(null)
        },
        onError: (err) => toastApiError(err, 'Failed to remove line item'),
      })
    },
    [deleteMut],
  )

  // WPS-165 #11/#12: the claim's per-category proration coverage drives the
  // (conditional) Prorated Qty / Prorated Amount columns. They only appear when
  // a proration formula is in effect for this claim.
  const materialProrationPct = summary?.material_proration_pct ?? null
  const laborProrationPct = summary?.labor_proration_pct ?? null
  const inProration = materialProrationPct != null || laborProrationPct != null
  // WPS-242: each line's prorated quantity + amount come from the backend, so
  // the table shows what the payments pay (material in whole units when the
  // tenant rounds up; the amount stays the actual proration).
  const lineProrations = summary?.line_prorations

  // WPS-208 #11: Voucher Quantity is a conditional column, exactly like the
  // prorated pair above and the Coverage column below - it appears when the
  // claim actually uses vouchers and is absent when it does not, so the nine
  // columns the ticket specifies are never pushed out of order by a field the
  // claim never uses.
  //
  // Driven by the data, not by a claim-type list: a line carrying a voucher
  // quantity shows the column, and nothing else does. Deliberately NOT gated on
  // the dialog's ``dialog_fields`` allowlist - that same allowlist decides
  // whether the Add Line Item card can CAPTURE a voucher quantity, and OC's
  // entry there feeds the SAP settlement export. Hiding the column must not
  // cost them the capture field, so the two questions stay separate.
  const showVoucherQuantity = useMemo(
    () => lineItems.some((i) => Number(i.voucher_quantity) > 0),
    [lineItems],
  )

  // Optional columns follow the tenant's own settlement options: with no
  // payment methods or recipient types configured there is nothing to show.
  const showPaymentType = (options?.payment_types ?? []).length > 0
  const showRecipient = recipientTypeOptions.length > 0

  const columns = useMemo<DataTableColumn<SettlementLineItem>[]>(
    () => [
      {
        id: 'expand',
        header: '',
        cell: (row) => (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation()
              toggleExpand(row.uuid)
            }}
          >
            {expandedUuids.has(row.uuid) ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
          </Button>
        ),
      },
      // WPS-208 #11: the line-item view reads in one fixed order - Item Detail,
      // Product Name, Quantity, Prorated Qty, Payment Type, Unit Price,
      // Extended Price, Ledger, Recipient - so a reviewer scanning a settlement
      // always finds the same figure in the same place. Unit of measure moved
      // into the expanded row detail alongside the rest of the product facts.
      //
      // Prorated Qty is the one conditional column INSIDE that run, because the
      // ticket lists it there. Every other optional column - Prorated Amount,
      // Voucher Quantity, Coverage - trails the fixed set, so nothing the claim
      // does not use can ever push the named columns out of sequence.
      {
        id: 'item_detail',
        header: t('settlement.lineItems.itemDetail', 'Item Detail'),
        cell: (row) => {
          const detail = row.item_detail ?? row.category
          const label =
            itemDetailOptions.find((o) => o.value === detail)?.label ?? detail
          return (
            <Badge variant={itemDetailBadgeVariant(detail, options)}>
              {label}
            </Badge>
          )
        },
      },
      {
        id: 'product_name',
        header: t('settlement.lineItems.productName', 'Product Name'),
        // X8: an Item Detail that captures no product (Non-OC Material) reads "-".
        cell: (row) => {
          const mapped = dialogFieldsForItem(row.item_detail ?? '', options)
          const name = !mapped || mapped.has('product_name') ? row.product_name : null
          return <span className="text-[var(--ink-2)]">{name || '-'}</span>
        },
      },
      {
        id: 'quantity',
        align: 'right',
        header: t('settlement.lineItems.quantity', 'Quantity'),
        // DRF serializes DecimalField as a fixed-precision string
        // ("2.0000") - render a plain human number instead. Guard
        // empties first: Number(null/'') coerces to 0, not NaN.
        cell: (row) => <span>{formatQuantity(row.quantity)}</span>,
      },
      // WPS-165 #11: prorated quantity, shown only when the claim is in
      // proration. Non-OC material / OTHER lines are not prorated (helper
      // returns null), so they read "-".
      ...(inProration
        ? [
            {
              id: 'prorated_quantity',
              align: 'right',
              header: t('claims.settlement.proratedQty', 'Prorated Qty'),
              cell: (row: SettlementLineItem) => {
                const proratedQuantity = lineProrations?.[row.uuid]?.quantity
                if (proratedQuantity == null) {
                  return <span className="text-[var(--ink-3)]">-</span>
                }
                return <span>{Number(proratedQuantity).toLocaleString()}</span>
              },
            } satisfies DataTableColumn<SettlementLineItem>,
          ]
        : []),
      ...(showPaymentType
        ? [
            {
              id: 'payment_type',
              header: t('settlement.lineItems.paymentType', 'Payment Type'),
              cell: (row: SettlementLineItem) => {
                if (!row.payment_type) {
                  return <span className="text-[var(--ink-3)]">-</span>
                }
                const label =
                  getAllowedPaymentTypes(row.item_detail || '', options).find(
                    (o) => o.value === row.payment_type,
                  )?.label ?? row.payment_type
                return <Badge variant="outline">{label}</Badge>
              },
            } satisfies DataTableColumn<SettlementLineItem>,
          ]
        : []),
      {
        id: 'unit_price',
        align: 'right',
        header: t('settlement.lineItems.unitPrice', 'Unit Price'),
        getValue: (row) => row.unit_price,
        field: moneyField,
      },
      {
        id: 'extended_price',
        align: 'right',
        header: t('settlement.lineItems.extendedPrice', 'Extended Price'),
        cell: (row) => {
          const isNegative = row.extended_price < 0
          return (
            <span className={isNegative ? 'text-[var(--color-destructive)]' : ''}>
              {formatCurrency(row.extended_price, currency, locale)}
            </span>
          )
        },
      },
      {
        id: 'ledger_type',
        header: t('settlement.lineItems.ledger', 'Ledger'),
        cell: (row) => {
          const ledger = row.ledger_type
          if (!ledger) return <span className="text-[var(--ink-3)]">-</span>
          const label = ledgerTypeOptions.find((l) => l.value === ledger)?.label ?? ledger
          return (
            <Badge
              variant="outline"
              className={ledgerPillClass(ledgerBadgeVariant(ledger, options))}
            >
              {label}
            </Badge>
          )
        },
      },
      ...(showRecipient
        ? [
            {
              id: 'recipient_type',
              header: t('settlement.lineItems.recipient', 'Recipient'),
              cell: (row: SettlementLineItem) => {
                if (!row.recipient_type) {
                  return <span className="text-[var(--ink-3)]">-</span>
                }
                const label =
                  recipientTypeOptions.find((o) => o.value === row.recipient_type)
                    ?.label ?? row.recipient_type
                return <Badge variant="outline">{label}</Badge>
              },
            } satisfies DataTableColumn<SettlementLineItem>,
          ]
        : []),
      // WPS-165 #11/#12: prorated amount (the line's actual proration), shown only
      // when the claim is in proration. This is the payable figure under the
      // warranty; Non-OC material / OTHER read "-" (paid in full at Extended).
      ...(inProration
        ? [
            {
              id: 'prorated_amount',
              align: 'right',
              header: t('claims.settlement.proratedAmount', 'Prorated Amount'),
              cell: (row: SettlementLineItem) => {
                const proratedAmount = lineProrations?.[row.uuid]?.amount
                if (proratedAmount == null) {
                  return <span className="text-[var(--ink-3)]">-</span>
                }
                return (
                  <span>{formatCurrency(Number(proratedAmount), currency, locale)}</span>
                )
              },
            } satisfies DataTableColumn<SettlementLineItem>,
          ]
        : []),
      ...(showVoucherQuantity
        ? [
            {
              id: 'voucher_quantity',
              align: 'right',
              header: t(
                'settlement.lineItems.voucherQuantity',
                'Voucher Quantity',
              ),
              cell: (row: SettlementLineItem) => (
                <span className="text-[var(--ink-2)]">
                  {formatQuantity(row.voucher_quantity)}
                </span>
              ),
            } satisfies DataTableColumn<SettlementLineItem>,
          ]
        : []),
      ...(hasCoverageDecisions
        ? [
            {
              id: 'coverage_decision',
              header: t('settlement.lineItems.coverage', 'Coverage'),
              cell: (row: SettlementLineItem) => {
                if (!row.coverage_decision) {
                  return <span className="text-[var(--ink-3)]">-</span>
                }
                const label =
                  coverageDecisionOptions.find(
                    (o) => o.value === row.coverage_decision,
                  )?.label ?? row.coverage_decision
                return <Badge variant="outline">{label}</Badge>
              },
            } satisfies DataTableColumn<SettlementLineItem>,
          ]
        : []),
      ...(isEditable
        ? [
            {
              id: 'actions',
              header: '',
              // Pinned right: this table is far wider than the viewport, so
              // unpinned these buttons sit a long scroll off-screen and the
              // row looks like it cannot be edited at all.
              sticky: 'right',
              cell: (row: SettlementLineItem) =>
                !row.is_locked ? (
                  <div className="flex items-center gap-1">
                    {/* Icon-only buttons need an accessible name - without one
                        a screen reader announces nothing but "button" - and a
                        28px target rather than 24px, which is the difference
                        between comfortable and fiddly for most people. */}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t(
                        'claims.settlement.editLine',
                        'Edit this line item',
                      )}
                      title={t('claims.settlement.editLine', 'Edit this line item')}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenEdit(row)
                      }}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t(
                        'claims.settlement.deleteLine',
                        'Delete this line item',
                      )}
                      title={t(
                        'claims.settlement.deleteLine',
                        'Delete this line item',
                      )}
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeletingUuid(row.uuid)
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ) : null,
            } satisfies DataTableColumn<SettlementLineItem>,
          ]
        : []),
    ],
    [
      isEditable,
      moneyField,
      handleOpenEdit,
      expandedUuids,
      toggleExpand,
      currency,
      locale,
      showVoucherQuantity,
      t,
      options,
      itemDetailOptions,
      ledgerTypeOptions,
      recipientTypeOptions,
      showPaymentType,
      showRecipient,
      hasCoverageDecisions,
      coverageDecisionOptions,
      inProration,
      lineProrations,
    ],
  )

  // ── Refresh Prices ──────────────────────────────────────
  // Iterates line items and looks up current pricing from the
  // geo-zone-based pricing engine. Material items use the
  // material price lookup; labor items use the labor rate lookup.
  const handleRefreshPrices = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false
    if (refreshing || lineItems.length === 0) return
    setRefreshing(true)

    const materialItems = lineItems.filter(
      (item) => !item.is_locked && materialItemDetailsSet.has(item.item_detail ?? item.category),
    )
    const laborItemsList = lineItems.filter(
      (item) => !item.is_locked && laborItemDetailsSet.has(item.item_detail ?? item.category),
    )

    let updated = 0
    let errors = 0

    // Refresh material items
    for (const item of materialItems) {
      if (!item.product_line) {
        continue
      }
      try {
        const result = await materialPriceLookup.lookup(
          item.product_line,
          item.product_name || undefined,
          item.product_code || undefined,
        )
        if (result?.price) {
          const newPrice = parseFloat(result.price)
          if (!isNaN(newPrice) && newPrice !== item.unit_price) {
            await updateMut.mutateAsync({
              itemUuid: item.uuid,
              data: {
                category: item.category,
                item_detail: item.item_detail ?? item.category,
                description: item.description,
                product_code: item.product_code || '',
                quantity: item.quantity,
                unit_price: newPrice,
                ledger_type: item.ledger_type || null,
                notes: item.notes || '',
                uom: result.uom || item.uom || null,
                voucher_quantity: item.voucher_quantity ?? null,
                sap_short_code: result.sap_short_code || item.sap_short_code || null,
                product_name: item.product_name || null,
                color: item.color || null,
                product_line: item.product_line || null,
                work_type: item.work_type || null,
                payee: item.payee || null,
                payee_address: item.payee_address || null,
                payment_type: item.payment_type || null,
                payment_date: item.payment_date || null,
                recipient_type: item.recipient_type || null,
              },
            })
            updated++
          }
        }
      } catch {
        errors++
      }
    }

    // Refresh labor items
    for (const item of laborItemsList) {
      // Product line is optional (WPS-161): a labor item with a work type but no
      // product line still resolves the any-product-line rate.
      if (!item.work_type) {
        continue
      }
      try {
        const result = await laborRateLookup.lookup(item.work_type, item.product_line)
        if (result?.rate_per_square) {
          const newRate = parseFloat(result.rate_per_square)
          // WPS-175 §8: compare against the PUBLISHED rate, not the line's
          // unit price - an uplifted line's unit price is the total, so
          // comparing to it would re-price the line on every refresh and
          // silently discard the increase.
          const currentSystemRate = Number(
            item.labor_system_rate ?? item.unit_price,
          )
          if (!isNaN(newRate) && newRate !== currentSystemRate) {
            await updateMut.mutateAsync({
              itemUuid: item.uuid,
              data: {
                category: item.category,
                item_detail: item.item_detail ?? item.category,
                description: item.description,
                product_code: item.product_code || '',
                quantity: item.quantity,
                unit_price: newRate,
                ledger_type: item.ledger_type || null,
                notes: item.notes || '',
                uom: item.uom || null,
                // The refreshed figure is the new published rate; the server
                // re-adds any increase on top and re-derives the total, so the
                // uplift survives a re-price.
                labor_system_rate: String(newRate),
                additional_amount: item.additional_amount ?? null,
                reason_for_increase: item.reason_for_increase ?? null,
                sap_short_code: item.sap_short_code || null,
                product_name: item.product_name || null,
                color: item.color || null,
                product_line: item.product_line || null,
                work_type: item.work_type || null,
                payee: item.payee || null,
                payee_address: item.payee_address || null,
                payment_type: item.payment_type || null,
                payment_date: item.payment_date || null,
                recipient_type: item.recipient_type || null,
              },
            })
            updated++
          }
        }
      } catch {
        errors++
      }
    }

    setRefreshing(false)

    // WP1-62: stamp the refresh time only when a price actually changed,
    // so the displayed timestamp reflects the last real price update.
    if (updated > 0) {
      try {
        await markPricesRefreshed.mutateAsync()
      } catch {
        // Non-fatal: the prices still updated; only the timestamp is missed.
      }
    }

    if (silent) return

    if (updated > 0 && errors === 0) {
      toast.success(`Updated ${updated} item${updated > 1 ? 's' : ''} with latest pricing`)
    } else if (updated > 0 && errors > 0) {
      toast.warning(`Updated ${updated}, failed ${errors} - some items may need manual review`)
    } else if (errors > 0) {
      toast.error(`Failed to refresh ${errors} item${errors > 1 ? 's' : ''}`)
    } else {
      toast.info('All prices are already up to date')
    }
  }, [
    refreshing,
    lineItems,
    markPricesRefreshed,
    materialItemDetailsSet,
    laborItemDetailsSet,
    materialPriceLookup,
    laborRateLookup,
    updateMut,
  ])

  const handleOpenCreate = useCallback(() => {
    setEditing(null)
    setSelectedProductLine(null)
    setSelectedProductName(null)
    setDialogOpen(true)
  }, [])

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false)
    setEditing(null)
    setSelectedProductLine(null)
    setSelectedProductName(null)
  }, [])

  // Resolve initial item_detail from legacy category if needed
  const initialItemDetail =
    editing?.item_detail ?? legacyCategoryToItemDetail(editing?.category ?? '', options)

  const initial = useMemo(
    () => ({
      item_detail: initialItemDetail,
      category: editing?.category ?? '',
      coverage_decision: editing?.coverage_decision ?? '',
      description: editing?.description ?? '',
      product_line: editing?.product_line ?? '',
      product_name: editing?.product_name ?? '',
      color: editing?.color ?? '',
      product_code: editing?.product_code ?? '',
      sap_short_code: editing?.sap_short_code ?? '',
      work_type: editing?.work_type ?? '',
      // WPS-175 §7: a new line starts at the quantity the claim already knows.
      quantity: editing
        ? String(editing.quantity)
        : (claimContext?.default_quantity ?? ''),
      uom: editing?.uom ?? '',
      voucher_quantity:
        editing?.voucher_quantity != null ? String(editing.voucher_quantity) : '',
      // WPS-175 §8: Unit Price holds the PUBLISHED rate. An edited labour line
      // stores its total rate as the unit price, so the published rate is read
      // back from the uplift trail - otherwise reopening a line would show the
      // uplifted rate and add the increase to itself again on save.
      unit_price: editing
        ? String(editing.labor_system_rate ?? editing.unit_price)
        : '',
      additional_amount:
        editing?.additional_amount != null ? String(editing.additional_amount) : '',
      total_labor_rate:
        editing?.total_labor_rate != null ? String(editing.total_labor_rate) : '',
      reason_for_increase: editing?.reason_for_increase ?? '',
      ledger_type:
        editing?.ledger_type ??
        (initialItemDetail ? defaultLedgerForItem(initialItemDetail, options) : ''),
      profit_centre: editing?.profit_centre ?? '',
      company_code: editing?.company_code ?? '',
      payee: editing?.payee ?? '',
      // ADDRESS field: an object seeds the composite input; undefined lets
      // useForm seed an empty address on create.
      payee_address: editing?.payee_address ?? undefined,
      // Editing keeps the line's own value; a NEW line starts on the claim
      // type's default (null for claim types that name none).
      payment_type: editing?.payment_type ?? defaultPaymentType ?? '',
      payment_date: editing?.payment_date ?? '',
      recipient_type: editing?.recipient_type ?? '',
      notes: editing?.notes ?? '',
    }),
    [editing, initialItemDetail, options, claimContext, defaultPaymentType],
  )

  // ── Auto-fill layer (optional, additive) ──
  // Default the payee address from the resolved party by recipient_type.
  // Homeowner remittance defaults to the registered property / consumer
  // address already loaded (and cached) for this claim; other recipient types
  // (contractor, installer, group, ...) have no address source on this page, so
  // they fall through to manual entry. Reads from the query cache - no extra
  // fetch. The field works fully without this layer.
  const { data: claimData } = useClaim(claimUuid)
  const holdingUuid = claimData?.claim?.holding_uuid ?? null
  const { data: holdingData } = useHolding(holdingUuid)

  const payeeAddressDefaults = useMemo<Record<string, EditableAddressValue>>(() => {
    const defaults: Record<string, EditableAddressValue> = {}
    const attrs = claimData?.claim?.attribute_values as
      | Record<string, unknown>
      | undefined
    // Homeowner remittance defaults to the registered property / consumer
    // address already loaded (and cached) for this claim.
    const homeownerSource =
      (holdingData as { host?: { address?: unknown } } | undefined)?.host?.address ??
      (holdingData as { mailing_address?: unknown } | undefined)?.mailing_address ??
      claimData?.claim?.consumer_address ??
      attrs?.end_user_address
    if (homeownerSource) {
      const address = toAddressInputValue(homeownerSource)
      if (toPayeeAddressPayload(address)) defaults.HOMEOWNER = address
    }
    // WPS-164 #19/R22: on an uninstalled claim the supplier is the payee, so
    // default the Supplier remittance address from the ``supplier_address``
    // captured at claim creation. Generic - read by attribute key, same pattern
    // as ``end_user_address``; no tenant branch.
    if (attrs?.supplier_address) {
      const address = toAddressInputValue(attrs.supplier_address)
      if (toPayeeAddressPayload(address)) defaults.SUPPLIER = address
    }
    // WPS-175 §7: every other recipient the claim recorded - contractor,
    // supplier - comes from the claim context, resolved server-side by
    // attribute code. Applied after the two above so a locally-derived
    // address (the homeowner's registered property) still wins where we have
    // richer structured data than the claim captured.
    for (const [recipientType, entry] of Object.entries(
      claimContext?.payee_defaults ?? {},
    )) {
      if (defaults[recipientType] || entry.address == null) continue
      const address = toAddressInputValue(entry.address)
      if (toPayeeAddressPayload(address)) defaults[recipientType] = address
    }
    return defaults
  }, [holdingData, claimData, claimContext])

  // The payee NAME the claim recorded per recipient type (WPS-164 #19/R22 for
  // the supplier; WPS-175 §7 extends it to the contractor). Read from the
  // claim context by recipient type, so adding another payable party is a
  // config change rather than another named constant here.
  const payeeNameDefaults = useMemo<Record<string, string>>(() => {
    const names: Record<string, string> = {}
    for (const [recipientType, entry] of Object.entries(
      claimContext?.payee_defaults ?? {},
    )) {
      if (typeof entry.payee === 'string' && entry.payee.trim()) {
        names[recipientType] = entry.payee.trim()
      }
    }
    return names
  }, [claimContext])

  // WPS-263: recipient types whose payee is searched (a supplier from the
  // tenant's directory). A pick on a claim whose own field is still empty is
  // written back to it on save, so the card and the payment agree.
  const payeeLookups = useMemo<Record<string, PayeeLookup>>(
    () => claimContext?.payee_lookups ?? {},
    [claimContext],
  )
  // A new line opens each payee search on the party the claim already holds
  // (the supplier on its card), so the search and the prefilled Payee agree.
  // Editing a line leaves it empty: the line's own payee stands.
  const payeeLookupInitial = useMemo<Record<string, unknown>>(() => {
    const attrs = claimData?.claim?.attribute_values as
      | Record<string, unknown>
      | undefined
    return Object.fromEntries(
      Object.entries(payeeLookups).map(([recipientType, lookup]) => [
        `payee_lookup_${recipientType}`,
        editing ? null : asSearchPick(attrs?.[lookup.code_name]),
      ]),
    )
  }, [payeeLookups, claimData, editing])
  const editClaimDirect = useEditClaimDirect()

  // Tracks the exact payee_address WE auto-filled (JSON of its components) so a
  // later recipient-type change can safely replace or clear it - but never
  // overwrite an address the operator typed themselves. Reset when the dialog
  // (re)opens so each line item starts clean.
  const autoFilledAddressRef = useRef<string | null>(null)
  // The payment type WE set from a recipient's default, so switching recipient
  // replaces only ours and never a choice the operator made.
  const autoFilledPaymentTypeRef = useRef<string | null>(null)
  // Likewise the payee name we filled, so a group's name never rides along
  // onto a homeowner payment.
  const autoFilledPayeeRef = useRef<string | null>(null)
  useEffect(() => {
    autoFilledAddressRef.current = null
    autoFilledPaymentTypeRef.current = null
    autoFilledPayeeRef.current = null
    // An editing line already carries a stored unit, so it starts satisfied;
    // a fresh line has resolved nothing yet.
    setUomResolved(editing?.uom ? true : null)
    setLaborRateResolved(null)
  }, [dialogOpen, editing?.uuid, editing?.uom])

  const handleRecipientTypeChange = useCallback(
    (value: unknown, formContext?: FormContext) => {
      if (!formContext) return
      const preset = payeeAddressDefaults[value as string] ?? null
      const currentPayload = toPayeeAddressPayload(
        formContext.values['payee_address'],
      )
      const currentJson = currentPayload ? JSON.stringify(currentPayload) : null
      const isEmpty = currentJson === null
      // Only change the address when it is empty or still holding OUR auto-fill;
      // a manual entry (non-empty and not our last auto-fill) is left untouched.
      const isOurAutoFill =
        currentJson !== null && currentJson === autoFilledAddressRef.current
      if (!isEmpty && !isOurAutoFill) return

      if (preset) {
        formContext.setFieldValue('payee_address', preset)
        autoFilledAddressRef.current = JSON.stringify(toPayeeAddressPayload(preset))
      } else if (isOurAutoFill) {
        // Switched to a recipient type with no known address: clear ours so a
        // homeowner address never rides along on a contractor / other payment.
        formContext.setFieldValue('payee_address', toAddressInputValue(null))
        autoFilledAddressRef.current = null
      }

      // Default the payee NAME from whatever the claim recorded for this
      // recipient type, so the whole payee prefills rather than half of it.
      // Never overwrite a name the operator already typed.
      // Never overwrite a name the operator typed; one we filled follows the
      // recipient type.
      const presetName = payeeNameDefaults[value as string]
      const currentPayee = String(formContext.values['payee'] ?? '')
      const ownsPayee = !currentPayee || currentPayee === autoFilledPayeeRef.current
      if (ownsPayee) {
        if (presetName) {
          formContext.setFieldValue('payee', presetName)
          autoFilledPayeeRef.current = presetName
        } else if (currentPayee) {
          formContext.setFieldValue('payee', '')
          autoFilledPayeeRef.current = null
        }
      }

      // And how this recipient is paid (e.g. a group by check), when the claim
      // context names it and the line's item detail allows it.
      const presetPaymentType =
        claimContext?.payee_defaults?.[value as string]?.payment_type ?? ''
      const currentPaymentType = String(formContext.values['payment_type'] ?? '')
      const ownsPaymentType =
        !currentPaymentType || currentPaymentType === autoFilledPaymentTypeRef.current
      if (ownsPaymentType) {
        const allowed = getAllowedPaymentTypes(
          String(formContext.values['item_detail'] ?? ''),
          options,
        )
        if (presetPaymentType && allowed.some((pt) => pt.value === presetPaymentType)) {
          formContext.setFieldValue('payment_type', presetPaymentType)
          autoFilledPaymentTypeRef.current = presetPaymentType
        } else if (currentPaymentType && autoFilledPaymentTypeRef.current) {
          formContext.setFieldValue('payment_type', '')
          autoFilledPaymentTypeRef.current = null
        }
      }
    },
    [payeeAddressDefaults, payeeNameDefaults, claimContext, options],
  )

  // Product-name-driven cascade (WPS-175 §7): price and unit of measure are
  // resolvable from the product base alone, so an operator who picks a product
  // gets both immediately rather than only after choosing a colour. Clears the
  // variant fields first so a half-changed line can never price one product at
  // another's rate.
  const handleProductNameChange = useCallback(
    (value: unknown, formContext?: FormContext) => {
      if (!formContext) return
      formContext.setFieldValue('color', '')
      formContext.setFieldValue('product_code', '')
      formContext.setFieldValue('sap_short_code', '')

      const productName = ((value as string | undefined) ?? '').trim()
      const productLine = (
        (formContext.values.product_line as string | undefined) ?? ''
      ).trim()
      if (!productLine || !productName) {
        setUomResolved(null)
        return
      }
      // Deferred a tick for the same reason as the cascades below: a same-batch
      // state update can re-render and reinitialise the form before the new
      // value commits.
      setTimeout(() => {
        void applyProductUom(formContext, productLine, productName)
        materialPriceLookup
          .lookup(productLine, productName)
          .then((result) => {
            if (!result?.price) return
            const price = parseFloat(result.price)
            if (!isNaN(price)) formContext.setFieldValue('unit_price', String(price))
            if (result.sap_short_code) {
              formContext.setFieldValue('sap_short_code', result.sap_short_code)
            }
          })
          .catch(() => {
            // Silent - pricing lookup is best-effort; the UoM check above is
            // the one that actually gates the save.
          })
      }, 0)
    },
    [applyProductUom, materialPriceLookup],
  )

  // WPS-162: OC-Material product selection. The base list comes from the
  // Material Pricing Table for the claim's geozone, so picking a base sets the
  // (hidden) product line it is priced under and pulls unit price + unit + SAP
  // code from that same table - the product library is never consulted for an
  // OC-Material line. Phase 1 selects the base only (no variant / colour).
  const handleMaterialBaseChange = useCallback(
    (value: unknown, formContext?: FormContext) => {
      if (!formContext) return
      const base = ((value as string | undefined) ?? '').trim()
      // Variant fields have no meaning in base-only mode; clear any stragglers.
      formContext.setFieldValue('color', '')
      formContext.setFieldValue('product_code', '')
      formContext.setFieldValue('sap_short_code', '')
      if (!base) {
        formContext.setFieldValue('product_line', '')
        setUomResolved(null)
        return
      }
      const matched = materialBaseByValue.get(base)
      const productLine = (matched?.product_line ?? '').trim()
      // The base carries its own product line (blank for a base-level rate);
      // set it so the row saves what it was priced under and the confirming
      // lookup below resolves the same rate.
      formContext.setFieldValue('product_line', productLine)
      // Fill unit + price straight from the option so the line is priced even
      // if the confirming lookup is slow or unavailable.
      if (matched?.uom) formContext.setFieldValue('uom', matched.uom)
      if (matched?.sap_short_code) {
        formContext.setFieldValue('sap_short_code', matched.sap_short_code)
      }
      if (matched?.price) {
        const price = parseFloat(matched.price)
        if (!isNaN(price)) formContext.setFieldValue('unit_price', String(price))
      }
      // A base drawn from the pricing table always carries the table's unit, so
      // the unit is resolved; never mark it unresolved here (that blocks save).
      setUomResolved(matched?.uom ? true : null)
      // The unit is the product's own fact: ask the resolver (catalogue first,
      // the price row only as its fallback) rather than trust the row's unit.
      void applyProductUom(formContext, productLine, base)
      // Confirm against the canonical resolver (it applies the ABC-Supply
      // supplier override the flat option list cannot). Deferred a tick for the
      // same reason as the other cascades.
      setTimeout(() => {
        materialPriceLookup
          .lookup(productLine, base)
          .then((result) => {
            // Guard against a stale lookup resolving after the operator has
            // already picked a different base - only apply if this base is
            // still the selected one.
            if (formContext.values.product_name !== base) return
            if (!result) return
            if (result.price) {
              const price = parseFloat(result.price)
              if (!isNaN(price)) {
                formContext.setFieldValue('unit_price', String(price))
              }
            }
            if (result.sap_short_code) {
              formContext.setFieldValue('sap_short_code', result.sap_short_code)
            }
          })
          .catch(() => {
            // Best-effort; the option values set above stand.
          })
      }, 0)
    },
    [applyProductUom, materialBaseByValue, materialPriceLookup],
  )

  // Color-driven cascade: resolve SKU, then run material pricing lookup.
  const handleColorChange = useCallback(
    (value: unknown, formContext?: FormContext) => {
      const color = ((value as string | undefined) ?? '').trim()
      if (!formContext || !color || !variants) return

      const variant = variants.find(
        (v) =>
          ((v.product_data?.color as string | undefined) || v.cached_short_name) === color,
      )
      if (!variant) return

      const sku = ((variant.product_data?.sku as string | undefined) ?? '').trim()
      if (sku) {
        formContext.setFieldValue('product_code', sku)
      }

      const productLine = ((formContext.values.product_line as string | undefined) ?? '').trim()
      const productName = ((formContext.values.product_name as string | undefined) ?? '').trim()
      if (!productLine || !sku) return

      // Defer pricing lookup - see comment in handleSkuVariantSelect for why
      // this can't run in the same batch as the setFieldValue above.
      setTimeout(() => {
        materialPriceLookup
          .lookup(productLine, productName || undefined, sku)
          .then((result) => {
            if (!result) return
            if (result.price) {
              const price = parseFloat(result.price)
              if (!isNaN(price)) formContext.setFieldValue('unit_price', String(price))
            }
            if (result.sap_short_code) {
              formContext.setFieldValue('sap_short_code', result.sap_short_code)
            }
          })
          .catch(() => {
            // Silent - pricing lookup is best-effort; user can fill manually.
          })
        // The unit is resolved separately from the price (WPS-175 §7): a
        // product can have an approved unit with no published price, and the
        // unit is what gates the save.
        void applyProductUom(formContext, productLine, productName, sku)
      }, 0)
    },
    [variants, materialPriceLookup, applyProductUom],
  )

  // SKU-driven cascade: autofill variant fields, then run material pricing lookup.
  const handleSkuVariantSelect = useCallback(
    (variant: SkuVariantMatch, formContext: FormContext) => {
      formContext.setFieldValue('product_code', variant.sku)
      if (variant.product_line) {
        formContext.setFieldValue('product_line', variant.product_line)
      }
      if (variant.product_name) {
        formContext.setFieldValue('product_name', variant.product_name)
      }
      if (variant.color) {
        formContext.setFieldValue('color', variant.color)
      }
      if (!variant.product_line || !variant.sku) return
      // Defer lookup to next tick: same-batch setLoading can re-render before
      // the form commits and trigger a reinitialisation reset of new values.
      setTimeout(() => {
        materialPriceLookup
          .lookup(variant.product_line, variant.product_name || undefined, variant.sku)
          .then((result) => {
            if (!result) return
            if (result.price) {
              const price = parseFloat(result.price)
              if (!isNaN(price)) formContext.setFieldValue('unit_price', String(price))
            }
            if (result.sap_short_code) {
              formContext.setFieldValue('sap_short_code', result.sap_short_code)
            }
          })
          .catch(() => {
            // Silent - pricing lookup is best-effort.
          })
        void applyProductUom(
          formContext,
          variant.product_line,
          variant.product_name,
          variant.sku,
        )
      }, 0)
    },
    [materialPriceLookup, applyProductUom],
  )

  // Work-type-driven cascade (WPS-161): auto-fill a labor line's Unit Price from
  // the geo-zone labor-rate book the moment a Work Type is picked - the labor
  // analogue of the material cascades above. The backend resolves the claim's
  // property address -> geozone -> (work type + optional product line) -> rate
  // per square, so the operator never has to know or type the rate. Best-effort:
  // a claim whose address has no published rate leaves the field for manual entry.
  const handleWorkTypeChange = useCallback(
    (value: unknown, formContext?: FormContext) => {
      if (!formContext) return
      const workType = ((value as string | undefined) ?? '').trim()
      if (!workType) return
      // Product line is optional for labor (the field is material-only in this
      // dialog, so it is usually blank); a blank line resolves the
      // any-product-line rate server-side.
      const productLine = (
        (formContext.values.product_line as string | undefined) ?? ''
      ).trim()
      // Defer the lookup a tick - same reason as the material cascades: a
      // same-batch state update can re-render and reinitialise the form before
      // the new value commits.
      setTimeout(() => {
        laborRateLookup
          .lookup(workType, productLine || null)
          .then((result) => {
            const rate = parseFloat(result?.rate_per_square ?? '')
            if (isNaN(rate)) {
              // WPS-165 #10: a miss is an answer, not a no-op. Saying so is
              // what stops the operator reading an empty Unit Price as a bug
              // - and what makes a hand-entered rate a deliberate act.
              setLaborRateResolved(false)
              return
            }
            setLaborRateResolved(true)
            formContext.setFieldValue('unit_price', String(rate))
            // WPS-175 #8: "pull in the UOM". A labour rate is stored as
            // rate_per_square, so the unit is a property of the rate rather
            // than a choice - the API states it and the card fills it, instead
            // of a labour line saving with UOM blank.
            if (result?.uom) formContext.setFieldValue('uom', result.uom)
          })
          .catch(() => {
            // The lookup 404s when nothing is published for this zone and work
            // type, which is the common case rather than a fault - same answer
            // as an empty result, reported the same way.
            setLaborRateResolved(false)
          })
      }, 0)
    },
    [laborRateLookup],
  )

  // Auto-fill cascade: when item_detail changes, auto-default ledger
  // The Item Detail currently chosen in the form, which is not the same thing
  // as the one the dialog opened with. On Add Item the initial value is empty,
  // and an empty item detail means "no filter" - so anything keyed off
  // ``initial`` offers every option no matter what the user then picks.
  const [pickedItemDetail, setPickedItemDetail] = useState<string>(
    initial.item_detail ?? "",
  );
  useEffect(() => {
    // Keyed on dialogOpen as well as the initial value. On Add Item the
    // initial value is always "", so an effect watching it alone never fires
    // again - the second dialog would open still holding the first one's
    // selection, and offer payment types for an item detail nobody picked.
    if (dialogOpen) setPickedItemDetail(initial.item_detail ?? "");
  }, [dialogOpen, initial.item_detail]);

  const handleItemDetailChange = useCallback(
    (
      value: unknown,
      // The full FormContext, not just setFieldValue: this handler has to read
      // the current payment type to decide whether it survives the change.
      formContext?: FormContext,
    ) => {
      const detail = value as string
      if (!formContext) return
      const defaultLedger = defaultLedgerForItem(detail, options)
      if (defaultLedger) {
        formContext.setFieldValue('ledger_type', defaultLedger)
      }
      // Clear fields the new item detail does not use, so a value chosen for
      // the previous one can never ride along on a line it does not describe.
      setPickedItemDetail(detail)
      // A payment type legal on the previous item detail may not be legal on
      // this one - Voucher is valid on OC Material and not on a Handling Fee.
      // Retiring it here means the form can never submit a pairing the tenant
      // has ruled out, which the server now also rejects.
      const stillAllowed = getAllowedPaymentTypes(detail, options)
      const currentPaymentType = String(formContext.values.payment_type ?? '')
      if (
        currentPaymentType &&
        !stillAllowed.some((pt) => pt.value === currentPaymentType)
      ) {
        formContext.setFieldValue('payment_type', '')
      }
      if (!showsField(detail, 'product_line')) {
        formContext.setFieldValue('product_line', '')
        formContext.setFieldValue('product_name', '')
        formContext.setFieldValue('color', '')
        formContext.setFieldValue('sap_short_code', '')
        setSelectedProductLine(null)
        setSelectedProductName(null)
      } else if (onlyProductLine && !formContext.values.product_line) {
        formContext.setFieldValue('product_line', onlyProductLine)
        setSelectedProductLine(onlyProductLine)
      }
      if (!showsField(detail, 'work_type')) {
        formContext.setFieldValue('work_type', '')
        setLaborRateResolved(null)
      }
      if (!showsField(detail, 'additional_amount')) {
        formContext.setFieldValue('additional_amount', '')
        formContext.setFieldValue('reason_for_increase', '')
      }
      if (!showsField(detail, 'voucher_quantity')) {
        formContext.setFieldValue('voucher_quantity', '')
      }
      // The unit belongs to the product, so switching away from a product line
      // retires the previous answer rather than leaving a stale block in place.
      if (!showsField(detail, 'uom')) {
        formContext.setFieldValue('uom', '')
      }
      setUomResolved(null)
    },
    [options, showsField, onlyProductLine],
  )

  // Payment types allowed for the current item detail. Reused for the
  // field options and to require the field only when at least one exists.
  const allowedPaymentTypes = useMemo(
    () => getAllowedPaymentTypes(pickedItemDetail || '', options),
    [pickedItemDetail, options],
  )

  // Ledger types allowed for the current item detail (WPS-147 §7). A LABOR
  // item detail is restricted to the Warranty ledger; everything else offers
  // the full selectable ledger list.
  const allowedLedgerTypes = useMemo(
    () => getAllowedLedgerTypes(pickedItemDetail || '', options),
    [pickedItemDetail, options],
  )

  // Where the unit on this line came from. A labour line has no product at
  // all - its unit is a property of the published rate (labour is stored as
  // rate per square), so the product-mapping wording is not just unhelpful
  // there, it names a lookup that never ran. Keyed off the item detail the
  // operator has actually picked, so switching Material -> Labor re-explains
  // the field rather than leaving the previous kind's sentence behind.
  const uomHint = useMemo(() => {
    if (laborItemDetailsSet.has(pickedItemDetail)) {
      return t(
        'settlement.lineItems.uomHintLaborRate',
        "Set from the published labor rate for this claim's area",
      )
    }
    if (uomResolved === false) {
      return t('settlement.lineItems.uomHintMissing', 'No approved unit of measure is mapped for this product')
    }
    return uomResolved
      ? t('settlement.lineItems.uomHintAuto', 'Set automatically from the approved product mapping')
      : t('settlement.lineItems.uomHintChoose', 'Set automatically when the product has an approved unit, otherwise choose one')
  }, [laborItemDetailsSet, pickedItemDetail, uomResolved, t])

  // A labour line's unit is a property of the published rate, so it locks
  // with the rate; a product line locks once the mapping resolved its unit.
  const uomLocked = laborItemDetailsSet.has(pickedItemDetail)
    ? laborRateResolved === true
    : uomResolved === true

  // WPS-162: whether the picked item detail sources its product from the
  // Material Pricing Table (base-only selection) rather than the product
  // library. When true the Product Name control becomes a Product Base picker
  // fed by the pricing table, and the product line / colour / SKU controls are
  // hidden - Phase 1 selects the base alone.
  const productBaseMode = materialPricingItemDetailsSet.has(pickedItemDetail)

  const fields = useMemo<FormField[]>(
    () => [
      // ── Section: What is this? ──
      {
        type: 'custom' as const,
        name: '_section_what',
        label: '',
        className: 'col-span-3',
        render: () => (
          <div className="col-span-3 flex items-center gap-2 pt-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-3)]">
              What is this?
            </span>
            <Separator className="flex-1" />
          </div>
        ),
      },
      // Fine-grained item detail (OC). Hidden for tenants that only use the
      // simple 3-way category (e.g. Teknor multi-hose).
      {
        type: 'select',
        name: 'item_detail',
        label: t('settlement.lineItems.itemDetail', 'Item Detail'),
        value: initial.item_detail,
        required: hasItemDetails ? 'Item detail is required' : undefined,
        options: selectableItemDetails.map((o) => ({ value: o.value, label: o.label })),
        className: 'col-span-1',
        visible: () => hasItemDetails,
        onChange: handleItemDetailChange,
      },
      // WP1-60: for tenants with item_detail vocabulary, Description is
      // removed from the form (redundant with Item Detail; backend defaults
      // it from the item-detail label). Tenants WITHOUT item_detail keep the
      // simple category + description fallback below.
      {
        type: 'select',
        name: 'category',
        label: t('settlement.lineItems.category', 'Category'),
        value: initial.category,
        // Required only when shown (item_detail tenants hide it, and a hidden
        // required field silently blocks submit - the generated schema
        // validates every field regardless of `visible`). Mirrors item_detail.
        required: !hasItemDetails ? 'Category is required' : undefined,
        options: categoryOptions.map((o) => ({ value: o.value, label: o.label })),
        className: 'col-span-1',
        visible: () => !hasItemDetails,
      },
      {
        type: 'text',
        name: 'description',
        label: t('settlement.lineItems.description', 'Description'),
        value: initial.description,
        // Required only when shown (hidden for item_detail tenants - see the
        // category note above; a hidden required field silently blocks submit).
        required: !hasItemDetails ? 'Description is required' : undefined,
        className: 'col-span-2',
        visible: () => !hasItemDetails,
      },
      // Per-row coverage determination (multi-item working-out). Shown only
      // when the tenant has seeded the coverage_decision options.
      {
        type: 'select',
        name: 'coverage_decision',
        label: t('settlement.lineItems.coverageDecision', 'Coverage Decision'),
        value: initial.coverage_decision,
        options: coverageDecisionOptions.map((o) => ({
          value: o.value,
          label: o.label,
        })),
        className: 'col-span-1',
        visible: () => hasCoverageDecisions,
      },
      // SKU-driven cascade: autofill variant fields, then run material pricing lookup.
      {
        type: 'custom' as const,
        name: 'product_code',
        label: t('settlement.lineItems.productCode', 'Product Code'),
        className: 'col-span-2',
        // WPS-162: no SKU search for a material-pricing item - selection is the
        // product base alone, resolved from the pricing table (Phase 1).
        visible: (ctx) =>
          showsField(ctx.values.item_detail as string, 'product_code') &&
          !materialPricingItemDetailsSet.has(ctx.values.item_detail as string),
        render: (formContext) => {
          const skuValue = (formContext.values.product_code as string | undefined) ?? ''
          return (
            <div>
              <Label className="mb-1.5 block clara-label font-normal">
                Product Code (SKU)
              </Label>
              <SkuSearchCombobox
                value={skuValue}
                onChange={(v) => formContext.setFieldValue('product_code', v)}
                onSelectVariant={(variant) => handleSkuVariantSelect(variant, formContext)}
                placeholder={t(
                  'settlement.lineItems.skuSearchPlaceholder',
                  'Type SKU to search…',
                )}
                data-testid="settlement-sku-search"
              />
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                Type to look up product - picking a result auto-fills the fields below.
              </p>
            </div>
          )
        },
      },
      {
        type: 'text',
        name: 'sap_short_code',
        label: t('settlement.lineItems.sapShortCode', 'SAP Short Code'),
        value: initial.sap_short_code,
        disabled: true,
        hint: t('settlement.lineItems.autoPopulatedFromPricingLookupHint', 'Auto-populated from pricing lookup'),
        className: 'col-span-1',
        visible: (ctx) => showsField(ctx.values.item_detail as string, 'sap_short_code'),
      },
      // Color-driven cascade: resolve SKU, then run material pricing lookup.
      {
        type: 'select',
        name: 'product_line',
        label: t('settlement.lineItems.productLine', 'Product Line'),
        value: initial.product_line,
        options: productLineOptions,
        className: 'col-span-1',
        // WPS-162: hidden for a material-pricing item - the product base carries
        // its own line, set silently when a base is picked (Phase 1: base only).
        visible: (ctx) =>
          showsField(ctx.values.item_detail as string, 'product_line') &&
          !materialPricingItemDetailsSet.has(ctx.values.item_detail as string),
        onChange: (_value: unknown, formContext?: FormContext) => {
          if (!formContext) return
          formContext.setFieldValue('product_name', '')
          formContext.setFieldValue('color', '')
          formContext.setFieldValue('product_code', '')
          formContext.setFieldValue('sap_short_code', '')
        },
      },
      {
        type: 'select',
        // WPS-162: for a material-pricing item this is the Product Base picker,
        // fed from the Material Pricing Table for the claim's geozone (not the
        // product library); otherwise it is the product-library Product Name.
        name: 'product_name',
        label: productBaseMode
          ? t('settlement.lineItems.productBase', 'Product Base')
          : t('settlement.lineItems.productName', 'Product Name'),
        value: initial.product_name,
        options: productBaseMode ? materialBaseOptions : productNameOptions,
        className: 'col-span-1',
        visible: (ctx) => showsField(ctx.values.item_detail as string, 'product_name'),
        // WPS-175 §7: price and unit are known as soon as the product is,
        // without waiting for a colour to be chosen - OC prices at the product
        // base, so most lines never need the variant. WPS-162: a material-
        // pricing item resolves both entirely from the pricing table.
        onChange: productBaseMode ? handleMaterialBaseChange : handleProductNameChange,
      },
      {
        type: 'select',
        name: 'color',
        label: t('settlement.lineItems.color', 'Color'),
        value: initial.color,
        options: colorOptions,
        className: 'col-span-1',
        // WPS-162: no variant / colour for a material-pricing item (Phase 1).
        visible: (ctx) =>
          showsField(ctx.values.item_detail as string, 'color') &&
          !materialPricingItemDetailsSet.has(ctx.values.item_detail as string),
        onChange: handleColorChange,
      },
      {
        type: 'select',
        name: 'work_type',
        label: t('settlement.lineItems.workType', 'Work Type'),
        value: initial.work_type,
        options: workTypeChoices,
        className: 'col-span-1',
        visible: (ctx) => showsField(ctx.values.item_detail as string, 'work_type'),
        // Auto-fill Unit Price from the geo-zone labor-rate book (WPS-161).
        hint: t(
          'claims.settlement.workTypeHint',
          "Picking a work type pulls the labor rate for this claim's area",
        ),
        onChange: handleWorkTypeChange,
      },

      // ── Section: How much? ──
      {
        type: 'custom' as const,
        name: '_section_howmuch',
        label: '',
        className: 'col-span-3',
        render: () => (
          <div className="col-span-3 flex items-center gap-2 pt-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-3)]">
              How much?
            </span>
            <Separator className="flex-1" />
          </div>
        ),
      },
      {
        type: 'number',
        name: 'quantity',
        label: t('settlement.lineItems.quantity', 'Quantity'),
        value: initial.quantity,
        required: 'Quantity is required',
        // WPS-175 §7: starts at the quantity the claim already knows about -
        // the approved squares when the lab narrowed the claim, otherwise the
        // registered quantity - so it is confirmed rather than retyped.
        hint: quantityHint,
        className: 'col-span-1',
      },
      {
        type: 'select',
        name: 'uom',
        label: t('settlement.lineItems.uom', 'UOM'),
        value: initial.uom,
        options: uomOptions.map((o) => ({ value: o.value, label: o.label })),
        // WPS-206 AC6: locked once the approved mapping (or the rate) sets the
        // unit; with no match the operator picks from the full list.
        disabled: uomLocked,
        hint: uomHint,
        className: 'col-span-1',
        // A claim type that does not capture the unit takes it from the product.
        // Before an Item Detail is picked, shown only if some item captures one.
        visible: (ctx) => {
          const detail = ctx.values.item_detail as string
          if (detail) return showsField(detail, 'uom')
          return (
            !hasDialogFieldMapping(options) ||
            (options?.item_details ?? []).some(
              (o) => Array.isArray(o.dialog_fields) && o.dialog_fields.includes('uom'),
            )
          )
        },
      },
      {
        type: 'number',
        name: 'voucher_quantity',
        label: t('settlement.lineItems.voucherQuantity', 'Voucher Quantity'),
        value: initial.voucher_quantity,
        hint: t('settlement.lineItems.thePartOfThisLinePaidAsAVoucherIfAnyHint', 'The part of this line paid as a voucher, if any'),
        className: 'col-span-1',
        visible: (ctx) =>
          showsField(ctx.values.item_detail as string, 'voucher_quantity'),
      },
      {
        type: 'number',
        name: 'unit_price',
        label: t('settlement.lineItems.unitPrice', 'Unit Price'),
        value: initial.unit_price,
        required: 'Unit price is required',
        hint: unitPriceHint,
        className: 'col-span-1',
      },
      // WPS-175 §8: the labour uplift. Unit Price above holds the published
      // geozone rate; these three say what was added to it, what the line
      // therefore pays, and why. Only a labour line has them.
      {
        type: 'number',
        name: 'additional_amount',
        label: t('settlement.lineItems.additionalAmount', 'Additional Amount'),
        value: initial.additional_amount,
        hint: t('settlement.lineItems.addedToTheSystemLaborRateHint', 'Added to the system labor rate'),
        className: 'col-span-1',
        visible: (ctx) =>
          showsField(ctx.values.item_detail as string, 'additional_amount'),
      },
      {
        type: 'text',
        name: 'total_labor_rate',
        label: t('settlement.lineItems.totalLaborRate', 'Total Labor Rate'),
        value: initial.total_labor_rate,
        disabled: true,
        hint: t('settlement.lineItems.systemRatePlusAnyAdditionalAmountHint', 'System rate plus any additional amount'),
        className: 'col-span-1',
        visible: (ctx) =>
          showsField(ctx.values.item_detail as string, 'total_labor_rate'),
      },
      {
        type: 'select',
        name: 'reason_for_increase',
        label: t('settlement.lineItems.reasonForIncrease', 'Reason for Increase'),
        value: initial.reason_for_increase,
        options: reasonForIncreaseOptions.map((o) => ({
          value: o.value,
          label: o.label,
        })),
        // A tenant rule marks the field required itself; without one the
        // hint says when it is.
        hint: reasonForIncreaseConfig
          ? undefined
          : t('settlement.lineItems.requiredOnceAnAdditionalAmountIsEnteredHint', 'Required once an additional amount is entered'),
        configurationData: reasonForIncreaseConfig,
        className: 'col-span-1',
        // Required-ness is enforced on submit (and again server-side) rather
        // than declared here, because a conditionally-shown required field
        // blocks submit silently when it is hidden.
        visible: (ctx) =>
          asksReasonForIncrease(ctx.values.item_detail as string, ctx.values),
      },
      {
        type: 'select',
        name: 'ledger_type',
        label: t('settlement.lineItems.ledger', 'Ledger'),
        value: initial.ledger_type,
        options: allowedLedgerTypes.map((o) => ({ value: o.value, label: o.label })),
        hint: ledgerHint,
        className: 'col-span-1',
      },
      // WPS-175 §7: Profit Centre and Company Code are no longer typed here.
      // They are assigned from the product details by the tenant's routing
      // rules on every save, and shown read-only in the expanded row.

      // ── Section: Who and when? ──
      {
        type: 'custom' as const,
        name: '_section_whowhen',
        label: '',
        className: 'col-span-3',
        render: () => (
          <div className="col-span-3 flex items-center gap-2 pt-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-3)]">
              Who and when?
            </span>
            <Separator className="flex-1" />
          </div>
        ),
      },
      {
        type: 'select',
        name: 'recipient_type',
        label: t('settlement.lineItems.recipientType', 'Recipient Type'),
        value: initial.recipient_type,
        options: recipientTypeOptions.map((o) => ({ value: o.value, label: o.label })),
        // Auto-fill layer: default the payee address from the resolved party.
        onChange: handleRecipientTypeChange,
        className: 'col-span-1',
      },
      // WPS-263: search the payee first when the recipient type has a search
      // (a supplier from the tenant's directory); the pick fills Payee and
      // Payee Address, both still editable.
      ...Object.entries(payeeLookups).map(
        ([recipientType, lookup]): FormField => ({
          type: 'entity-search',
          name: `payee_lookup_${recipientType}`,
          label: lookup.display_name,
          placeholder: lookup.display_name,
          value: payeeLookupInitial[`payee_lookup_${recipientType}`] ?? null,
          configurationData: payeeLookupConfig(lookup),
          visible: (ctx) => ctx.values.recipient_type === recipientType,
          className: 'col-span-1',
        }),
      ),
      {
        type: 'text',
        name: 'payee',
        label: t('settlement.lineItems.payee', 'Payee'),
        value: initial.payee,
        maxLength: SAP_PAYEE_MAX_LENGTH,
        hint: `${SAP_PAYEE_MAX_LENGTH}-char SAP limit for check printing`,
        className: 'col-span-1',
      },
      {
        // Remittance address of the payee (any recipient type). Uses the shared
        // ADDRESS composite + app-wide address lookup; stored as one EAV value
        // and aggregated onto the payment by the backend.
        type: 'address',
        name: 'payee_address',
        label: t('settlement.lineItems.payeeAddress', 'Payee Address'),
        value: initial.payee_address,
        configurationData: { lookup_provider: 'address' },
        hint: t('settlement.lineItems.whereThePaymentIsSentHint', 'Where the payment is sent'),
        className: 'col-span-3',
      },
      {
        type: 'select',
        name: 'payment_type',
        label: t('settlement.lineItems.paymentType', 'Payment Type'),
        value: initial.payment_type,
        options: allowedPaymentTypes.map((o) => ({
          value: o.value,
          label: o.label,
        })),
        // Required before save, but only when the item detail has
        // allowed payment types (else the select would be unsatisfiable).
        required: allowedPaymentTypes.length
          ? 'Payment Type is required'
          : undefined,
        hint: hasItemDetails
          ? t('settlement.lineItems.filteredByItemDetailSelectionHint', 'Filtered by item detail selection')
          : undefined,
        className: 'col-span-1',
      },
      // Payment Date removed from line-item creation. "Date Added" is
      // captured automatically (created_at) and shown in the Line Items card.
      {
        type: 'textarea',
        name: 'notes',
        label: t('settlement.lineItems.notes', 'Notes'),
        value: initial.notes,
        // Only meaningful to a tenant that actually has the item_detail
        // vocabulary. Shown unconditionally it told every other tenant that a
        // field their form does not contain governs whether Notes is required.
        hint: hasItemDetails
          ? t('settlement.lineItems.requiredWhenItemDetailIsOtherHint', 'Required when Item Detail is "Other".')
          : undefined,
        className: 'col-span-3',
      },
    ],
    [
      initial,
      t,
      handleItemDetailChange,
      handleColorChange,
      handleRecipientTypeChange,
      handleSkuVariantSelect,
      handleWorkTypeChange,
      allowedPaymentTypes,
      workTypeChoices,
      unitPriceHint,
      uomOptions,
      selectableItemDetails,
      allowedLedgerTypes,
      ledgerHint,
      recipientTypeOptions,
      categoryOptions,
      coverageDecisionOptions,
      reasonForIncreaseOptions,
      reasonForIncreaseConfig,
      asksReasonForIncrease,
      hasItemDetails,
      hasCoverageDecisions,
      showsField,
      options,
      quantityHint,
      uomHint,
      uomLocked,
      handleProductNameChange,
      productLineOptions,
      productNameOptions,
      colorOptions,
      productBaseMode,
      materialBaseOptions,
      handleMaterialBaseChange,
      materialPricingItemDetailsSet,
      payeeLookups,
      payeeLookupInitial,
    ],
  )

  // Registry-driven slimming: keep only fields the tenant's
  // ``settlement_line_item`` registration defines (plus the always-on
  // core fields). Drop the "Who and when?" section header when none of
  // its fields survive.
  const visibleFields = useMemo<FormField[]>(() => {
    if (registryCodes.size === 0) return fields
    const kept = fields.filter((f) => {
      // FormField.name is optional on the shared type, and a section divider
      // genuinely has none - those are never registry-gated, so they stay.
      if (!f.name) return true
      return !REGISTRY_GATED_FIELDS.has(f.name) || registryCodes.has(f.name)
    })
    if (!WHO_WHEN_FIELDS.some((name) => kept.some((f) => f.name === name))) {
      return kept.filter((f) => f.name !== '_section_whowhen')
    }
    return kept
  }, [fields, registryCodes])

  // Called after a line is saved: a payee picked from a search, on a claim
  // whose own field for that party is still empty, is written to the claim so
  // its card shows the same company and address the payment goes to. Never
  // overwrites what the claim already holds. Read through a ref so the form's
  // submit handler always sees the latest claim.
  const savePayeeToClaimRef = useRef<(v: Record<string, unknown>) => Promise<void>>(
    async () => {},
  )
  savePayeeToClaimRef.current = async (v: Record<string, unknown>) => {
    const recipientType = (v.recipient_type as string) || ''
    const lookup = payeeLookups[recipientType]
    const pick = v[`payee_lookup_${recipientType}`] as
      | { uuid?: string; name?: string }
      | null
      | undefined
    if (!lookup || !pick?.uuid) return
    const attrs = claimData?.claim?.attribute_values as
      | Record<string, unknown>
      | undefined
    if (!isBlankValue(attrs?.[lookup.code_name])) return
    const values: Record<string, unknown> = { [lookup.code_name]: pick }
    const address = toPayeeAddressPayload(v.payee_address)
    if (lookup.address_code && address && isBlankValue(attrs?.[lookup.address_code])) {
      values[lookup.address_code] = address
    }
    try {
      await editClaimDirect.mutateAsync({
        uuid: claimUuid,
        attribute_values: values,
        edit_reason: t(
          'settlement.lineItems.payeeSavedToClaim',
          '{{field}} chosen as the payee of a line item',
          { field: lookup.display_name },
        ),
      })
    } catch {
      toast.warning(
        t(
          'settlement.lineItems.payeeNotSavedToClaim',
          'Line item added. The {{field}} was not saved to the claim, add it on the card.',
          { field: lookup.display_name.toLowerCase() },
        ),
      )
    }
  }

  const { Form, ref } = useForm(
    {
      name: `settlement-line-item-${editing ? 'edit' : 'create'}`,
      submit: 'footer',
      fields: visibleFields,
    },
    async (rawValues, formContext) => {
      const v = rawValues as Record<string, unknown>
      const itemDetail = (v.item_detail as string) || ''
      // With item_detail vocabulary, derive the legacy category from it;
      // otherwise the simple category select is the source of truth.
      const category = itemDetail
        ? resolveCategory({ item_detail: itemDetail }, options)
        : ((v.category as string) || 'OTHER')
      const notesValue = ((v.notes as string) ?? '').trim()

      // WP1-60: Notes are required when "Other" is the item detail. Surface it
      // as an inline field error (red Notes box + message beneath it) rather
      // than a transient toast, so the requirement is visible right where the
      // user must act. The error clears automatically as soon as they type.
      if (itemDetail === 'OTHER' && !notesValue) {
        formContext.setFieldError(
          'notes',
          t(
            'settlement.lineItems.notesRequiredForOther',
            'Notes are required when Item Detail is "Other".',
          ),
        )
        return
      }

      // WPS-175 §7: a product with no approved unit of measure cannot be
      // settled. Shown against the UOM field itself so the block is legible
      // where the missing value is, not as a toast that scrolls away.
      // Enforced only once the tenant has published an approved unit for
      // SOMETHING. Against an empty mapping table this rule refuses every
      // product, which would stop the tenant settling at all - so it stays
      // dormant until the table has content and then applies in full,
      // without anyone having to switch it on.
      if (
        showsField(itemDetail, 'uom') &&
        uomResolved === false &&
        uomMappingAvailable
      ) {
        formContext.setFieldError(
          'uom',
          t(
            'settlement.lineItems.noApprovedUom',
            'No approved unit of measure is mapped for this product. It cannot be added until the mapping exists.',
          ),
        )
        return
      }

      // WPS-175 §8: an uplift has to say why, and so does any line the
      // tenant's ``required_when`` rule names (WPS-207). Enforced again
      // server-side from the same config, so the rule holds whatever reaches
      // the API.
      if (
        asksReasonForIncrease(itemDetail, v) &&
        !((v.reason_for_increase as string) ?? '').trim()
      ) {
        formContext.setFieldError(
          'reason_for_increase',
          isConditionallyRequired(reasonForIncreaseConfig, v)
            ? t('settlement.lineItems.reasonForIncreaseIsRequired', 'Reason for Increase is required.')
            : t(
                'settlement.lineItems.reasonForIncreaseRequired',
                'Reason for Increase is required once an additional amount is entered.',
              ),
        )
        return
      }

      // Description: a form field only for tenants WITHOUT item_detail
      // vocabulary. For item_detail tenants the backend defaults it from the
      // item label on create; on edit we preserve the stored value.
      const description = hasItemDetails
        ? editing
          ? editing.description
          : ''
        : ((v.description as string) ?? '').trim()

      const payload: Record<string, unknown> = {
        category,
        item_detail: itemDetail || null,
        coverage_decision: (v.coverage_decision as string) || null,
        description,
        product_code: ((v.product_code as string) ?? '').trim(),
        quantity: v.quantity,
        unit_price: v.unit_price,
        // ``ledger_type`` is the semantic intent (WARRANTY / GOODWILL) the
        // tech picks. The backend's ``apply_ledger_routing`` in
        // ``apps/claims/services/ledger.py`` resolves the canonical GL
        // ``ledger_account`` from ``LedgerRoutingRule`` predicates - never
        // hardcoded here. Phase 6 of the tenant-agnostic claims migration.
        ledger_type: v.ledger_type || null,
        // WPS-175 §7: profit centre and company code are assigned by the
        // tenant's routing rules from the product details on every save, so
        // they are deliberately absent from this payload. Sending the stored
        // values back would just be shouting the rules' own answer at them.
        notes: notesValue,
        uom: v.uom || null,
        voucher_quantity: ((v.voucher_quantity as string) ?? '') || null,
        // WPS-175 §8: Unit Price above carries the PUBLISHED rate. The server
        // adds the increase to it, writes the sum as the line's unit price,
        // and keeps all three numbers so the uplift stays auditable.
        labor_system_rate: showsField(itemDetail, 'additional_amount')
          ? (v.unit_price ?? null)
          : null,
        additional_amount: hasAdditionalAmount(v.additional_amount)
          ? v.additional_amount
          : null,
        reason_for_increase: asksReasonForIncrease(itemDetail, v)
          ? v.reason_for_increase || null
          : null,
        payee: ((v.payee as string) ?? '').trim() || null,
        payee_address: toPayeeAddressPayload(v.payee_address),
        payment_type: v.payment_type || null,
        // WP1-60: Payment Date removed from line-item creation. On edit,
        // preserve any existing value so we never clear stored payment dates.
        payment_date: editing ? editing.payment_date : null,
        recipient_type: v.recipient_type || null,
        sap_short_code: ((v.sap_short_code as string) ?? '').trim() || null,
        product_name: ((v.product_name as string) ?? '').trim() || null,
        color: ((v.color as string) ?? '').trim() || null,
        product_line: ((v.product_line as string) ?? '').trim() || null,
        work_type: v.work_type || null,
      }

      try {
        if (editing) {
          await updateMut.mutateAsync({ itemUuid: editing.uuid, data: payload })
          toast.success('Line item updated')
        } else {
          await createMut.mutateAsync(payload)
          toast.success('Line item added')
        }
        await savePayeeToClaimRef.current(v)
        // WP1-60: prices refresh automatically after add/update (no manual click).
        // Fires once on the next lineItems refetch (see auto-refresh effect).
        autoRefreshRef.current = true
        handleDialogClose()
      } catch (err) {
        toastApiError(err, `Failed to ${editing ? 'update' : 'add'} line item`)
      }
    },
    [dialogOpen, editing?.uuid],
  )

  // Hard-reset on open - the form persists across reopens (a submit-close
  // skips any reset), so "+ Add Item" showed the prior line's values (cf WP1-71).
  useEffect(() => {
    if (!dialogOpen) return
    ref.current?.resetForm({ values: { ...initial, ...payeeLookupInitial } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, editing?.uuid])

  const busy = createMut.isPending || updateMut.isPending

  const formatOptions = { currency }

  // WP1-62: the claim's stage locks pricing once it leaves the editable
  // settlement stage; nothing flags the lines themselves on entering LOA.
  const lockedForApproval = isLocked
  const refreshedLabel = pricesRefreshedAt
    ? formatDateAdded(pricesRefreshedAt, locale)
    : ''
  const headerActions = isEditable ? (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="xs"
        onClick={() => handleRefreshPrices()}
        disabled={refreshing || lineItems.length === 0}
      >
        <RefreshCw size={12} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing
          ? t('settlement.lineItems.refreshing', 'Refreshing…')
          : t('settlement.lineItems.refreshPrices', 'Refresh Prices')}
      </Button>
      <Button variant="outline" size="xs" onClick={handleOpenCreate}>
        <Plus size={12} className="mr-1" />
        {t('settlement.lineItems.addItem', 'Add Item')}
      </Button>
    </div>
  ) : lockedForApproval ? (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--ink-3)]">
      <Lock size={11} className="text-[var(--rose)]" />
      {t(
        'settlement.lineItems.priceRefreshLocked',
        'Price refresh locked - sent to LOA',
      )}
    </span>
  ) : null

  return (
    <DetailCard
      title={t('settlement.lineItems.title', 'Line Items')}
      icon={<Layers className="h-3.5 w-3.5" />}
      collapsible={false}
      trailing={headerActions}
    >
      {lineItems.length > 0 ? (
        <DataTable
          tableId="settlement-line-items"
          columns={columns}
          data={lineItems}
          rowKey="uuid"
          formatOptions={formatOptions}
          emptyMessage="No line items yet"
          onRowClick={(row) => toggleExpand(row.uuid)}
          renderRowDetail={(row) =>
            expandedUuids.has(row.uuid) ? (
              <ExpandedDetailRow
                item={row}
                options={options}
                currency={currency}
              />
            ) : null
          }
          renderRowDetailStartColumn={2}
          // The detail strip is a long run of label/value pairs; left
          // unconstrained it sets the table's width and inflates every
          // column (a Qty of "20" was getting 148px). Fitting it to the
          // viewport lets it wrap and the columns shrink to their content.
          fitRowDetailToViewport
          flush
          dense
        />
      ) : (
        <div className="px-4 py-6 text-center text-[13px] font-light text-[var(--ink-3)]">
          No settlement line items.{' '}
          {isEditable && 'Click "Add Item" to begin building the settlement.'}
        </div>
      )}
      {lineItems.length > 0 && (
        <div className="flex items-center justify-center gap-1.5 border-t-[0.5px] border-t-[var(--rule-light)] py-2.5 text-[11px] text-[var(--ink-3)]">
          <span>
            {lineItems.length === 1
              ? t('settlement.lineItems.oneLine', '1 line')
              : t('settlement.lineItems.lineCount', '{{count}} lines', {
                  count: lineItems.length,
                })}
          </span>
          {refreshedLabel && (
            <span>
              ·{' '}
              {t('settlement.lineItems.pricesRefreshed', 'Prices refreshed {{date}}', {
                date: refreshedLabel,
              })}
            </span>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit Line Item' : 'Add Line Item'}
            </DialogTitle>
            <DialogDescription>
              Fields auto-populate based on your selections. Only fill what you need.
            </DialogDescription>
          </DialogHeader>
          <Form className="grid grid-cols-3 gap-x-4 gap-y-3 py-2">
            <CascadeSync onSync={handleCascadeSync} />
            <LaborRateSync />
          </Form>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDialogClose}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={() => ref.current?.submitForm()}
              disabled={busy}
            >
              {busy ? 'Saving…' : editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deletingUuid !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingUuid(null)
        }}
        title="Remove line item?"
        description="This settlement line item will be permanently removed."
        isPending={deleteMut.isPending}
        onConfirm={() => {
          if (deletingUuid) handleDelete(deletingUuid)
        }}
      />
    </DetailCard>
  )
}
